import React, { useCallback, useEffect, useMemo, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";

import DashboardLayout from "examples/LayoutContainers/DashboardLayout";
import DashboardNavbar from "examples/Navbars/DashboardNavbar";
import Footer from "examples/Footer";
import DataTable from "examples/Tables/DataTable";

import MDBox from "components/MDBox";
import MDTypography from "components/MDTypography";
import MDButton from "components/MDButton";

import { Avatar, Card, Chip, Dialog, DialogActions, DialogContent, DialogTitle, Divider, Grid, IconButton, InputAdornment, LinearProgress, MenuItem, Skeleton, Tooltip } from "@mui/material";
// Büyük formda her tuşta tüm formun yeniden çizilmesini önleyen tamponlu TextField (yazma gecikmesi)
import TextField from "components/BufferedTextField";

import {
    AccountBalance,
    Add,
    ArrowUpward,
    CheckCircle,
    Download,
    DirectionsBoat,
    FilterAlt,
    Flight,
    Inventory2,
    LocalShipping,
    Paid,
    ReceiptLong,
    Refresh,
    RestartAlt,
    Search,
    Visibility,
    WarningAmber,
} from "@mui/icons-material";

import FinanceStatusChip from "../components/FinanceStatusChip";
import { formatCurrency, formatDate } from "../utils/formatter";
import {
    COLORS,
    cardSx,
    FinanceCard,
    FinanceSectionHeader,
    FinanceKpiTile,
    FinanceHeroStat,
    SPACE,
    FinanceDetailsSection,
} from "../shared/FinanceUiKit";
import { toLocalDateInputValue } from "../shared/dateInput";
import { getVatAmount, hasVatIncluded } from "../shared/vatDisplay";

import {
    financeText,
    getFinanceLanguage,
    saveFinanceLanguage,
} from "finance-language";
import { appAlert } from "components/AppDialog";
// Koyu hero bandı için (yedekten geri yüklendi)
import { WorkspacePremium } from "@mui/icons-material";
import { CalendarMonth } from "@mui/icons-material";

const API_URL = process.env.REACT_APP_API_URL;

const getValue = (row, camel, snake, fallback = "") =>
    row?.[camel] ?? row?.[snake] ?? fallback;

const toNumber = (value) => {
    const parsed = Number(value);
    return Number.isFinite(parsed) ? parsed : 0;
};

const firstDefinedNumber = (...values) => {
    const value = values.find(
        (item) => item !== undefined && item !== null && item !== "",
    );

    return toNumber(value);
};

const getBuyingRate = (item) =>
    firstDefinedNumber(
        item?.buyingRate,
        item?.buying_rate,
        item?.financialSummary?.buyingRate,
        item?.financial_summary?.buying_rate,
        item?.freightCharges?.buyingRate,
        item?.freight_charges?.buying_rate,
    );

const getOperationalCost = (item) => {
    const summary = item?.financialSummary || item?.financial_summary || {};

    const freightCharges = item?.freightCharges || item?.freight_charges || {};

    const explicitOperationalCost = firstDefinedNumber(
        item?.operationalCost,
        item?.operational_cost,
        summary?.operationalCost,
        summary?.operational_cost,
    );

    if (explicitOperationalCost > 0) {
        return explicitOperationalCost;
    }

    const roadCost =
        firstDefinedNumber(summary?.shipmentCostTotal) +
        firstDefinedNumber(summary?.convertedLinehaulCost) +
        firstDefinedNumber(summary?.convertedHandlingCost);

    const oceanCost =
        firstDefinedNumber(summary?.pickupTotal) +
        firstDefinedNumber(summary?.othcTotal) +
        firstDefinedNumber(summary?.oceanFreightTotal) +
        firstDefinedNumber(summary?.dthcTotal) +
        firstDefinedNumber(summary?.handlingTotal) +
        firstDefinedNumber(summary?.deliveryTotal);

    const airCost =
        firstDefinedNumber(summary?.pickupTotal) +
        firstDefinedNumber(summary?.othcTotal) +
        firstDefinedNumber(summary?.airFreightTotal) +
        firstDefinedNumber(summary?.dthcTotal) +
        firstDefinedNumber(summary?.handlingTotal) +
        firstDefinedNumber(summary?.deliveryTotal);

    const freightChargeCost =
        firstDefinedNumber(freightCharges?.pickupTotal) +
        firstDefinedNumber(freightCharges?.othc?.total) +
        firstDefinedNumber(freightCharges?.oceanFreight?.total) +
        firstDefinedNumber(freightCharges?.airFreight?.total) +
        firstDefinedNumber(freightCharges?.dthc?.total) +
        firstDefinedNumber(freightCharges?.handling?.total) +
        firstDefinedNumber(freightCharges?.deliveryTotal);

    if (roadCost > 0) return roadCost;
    if (oceanCost > 0) return oceanCost;
    if (airCost > 0) return airCost;
    if (freightChargeCost > 0) return freightChargeCost;

    return 0;
};

const getTotalCost = (item) => {
    const explicitTotalCost = firstDefinedNumber(
        item?.actualCostTotal,
        item?.actual_cost_total,
        item?.totalCost,
        item?.total_cost,
        item?.financialSummary?.totalCost,
        item?.financial_summary?.total_cost,
    );

    const invoiceItems =
        [item?.invoiceItems, item?.invoice_items].find(Array.isArray) || [];
    const invoiceItemCost = invoiceItems.length
        ? invoiceItems.reduce(
            (sum, invoiceItem) =>
                sum +
                firstDefinedNumber(
                    invoiceItem?.totalCost,
                    invoiceItem?.total_cost,
                    invoiceItem?.cost,
                    invoiceItem?.unitCost,
                    invoiceItem?.unit_cost,
                ),
            0,
        )
        : 0;

    // Backend total cost is authoritative. Adding buyingRate again here would
    // double count the shared Finance Job cost on multi-invoice jobs.
    if (explicitTotalCost > 0) return explicitTotalCost;

    if (invoiceItemCost > 0) return invoiceItemCost;

    return getOperationalCost(item) + getBuyingRate(item);
};

const getAmount = (item) =>
    Number(
        item?.paidAmount ||
        item?.paid_amount ||
        item?.collectedAmount ||
        item?.collected_amount ||
        item?.amount ||
        0,
    );

// VAT-exclusive selling rate: the revenue figure profit and margin use.
const getExVatInvoiceAmount = (item) =>
    Number(
        item?.invoiceSalesAmount ||
        item?.salesAmount ||
        item?.customerInvoiceAmount ||
        item?.totalAmount ||
        item?.total_amount ||
        item?.amount ||
        0,
    );

// What the customer is billed: VAT included when the invoice carries VAT.
// The collectible balance, net amount and credit all build on this, so a
// VAT invoice can be collected in full (e.g. 900 on a 750 + 150 VAT invoice).
const getInvoiceAmount = (item) =>
    hasVatIncluded(item)
        ? Number(item?.sellingRateInclVat || 0)
        : getExVatInvoiceAmount(item);

const getPaidAmount = (item) =>
    Number(item?.paidAmount || item?.paid_amount || item?.collectedAmount || 0);

const normalizeCurrencyCode = (currency) => {
    const code = String(currency || "EUR")
        .trim()
        .toUpperCase();
    if (code === "EURO") return "EUR";
    if (code === "TL") return "TRY";
    return code;
};

// Currencies offered by the "Display Currency" selector -- the currency every
// aggregated/blended figure on this page (KPI cards, hero stat, mode chart,
// CSV export) is converted into. NOT to be confused with `currencyFilter`
// below, which filters WHICH records are listed by their own currency.
const CURRENCY_OPTIONS = ["EUR", "USD", "TRY", "GBP"];

const getCurrency = (item) =>
    normalizeCurrencyCode(
        item?.sellingRateCurrency ||
        item?.selling_rate_currency ||
        item?.currency ||
        item?.invoiceCurrency ||
        item?.invoice_currency ||
        item?.financialSummary?.sellingRateCurrency ||
        item?.financial_summary?.selling_rate_currency ||
        item?.financialSummary?.totalCurrency ||
        item?.financial_summary?.total_currency ||
        item?.freightCharges?.totalCurrency ||
        item?.freight_charges?.total_currency ||
        "EUR",
    );

const getCostCurrency = (item) => {
    const items =
        [
            item?.invoiceItems,
            item?.invoice_items,
            item?.cargoDetails,
            item?.cargo_details,
        ].find(Array.isArray) || [];
    const itemWithCurrency = items.find(
        (invoiceItem) => invoiceItem?.costCurrency || invoiceItem?.cost_currency,
    );
    const itemCurrency =
        itemWithCurrency?.costCurrency || itemWithCurrency?.cost_currency;

    return normalizeCurrencyCode(
        item?.costCurrency ||
        item?.cost_currency ||
        item?.totalCostCurrency ||
        item?.total_cost_currency ||
        itemCurrency ||
        item?.financialSummary?.costCurrency ||
        item?.financial_summary?.cost_currency ||
        item?.financialSummary?.totalCurrency ||
        item?.financial_summary?.total_currency ||
        "EUR",
    );
};

const getShipmentNo = (item) =>
    getValue(item, "jobNo", "job_no") ||
    item?.shipmentNo ||
    item?.shipment_no ||
    item?.invoiceNo ||
    item?.invoice_no ||
    "N/A";

const getCustomerName = (item) =>
    getValue(item, "customerName", "customer_name", item?.customer || "N/A");

const getSupplierName = (item) =>
    item?.supplierName ||
    item?.supplier_name ||
    item?.serviceProviderName ||
    item?.service_provider_name ||
    item?.linehaulServiceProvider ||
    item?.supplier?.companyName ||
    item?.serviceProvider?.companyName ||
    "N/A";

const getDateValue = (item) => {
    const raw =
        item?.collectionDate ||
        item?.collection_date ||
        item?.paymentDate ||
        item?.payment_date ||
        item?.createdAt ||
        item?.created_at ||
        item?.updatedAt ||
        item?.updated_at;

    const date = raw ? new Date(raw) : null;
    return date && !Number.isNaN(date.getTime()) ? date : null;
};

const normalizeCountry = (value) =>
    String(value || "")
        .trim()
        .toLocaleUpperCase("tr-TR")
        .replace(/İ/g, "I")
        .replace(/Ü/g, "U");
const normalizeLookupKey = (value) => {
    if (!value) return "";
    if (typeof value === "object")
        return normalizeLookupKey(
            value?._id ||
            value?.id ||
            value?.$oid ||
            value?.nameKey ||
            value?.name ||
            value?.companyName,
        );
    return String(value)
        .trim()
        .toLocaleUpperCase("tr-TR")
        .normalize("NFKD")
        .replace(/[\u0300-\u036f]/g, "")
        .replace(/[^A-Z0-9]+/g, " ")
        .trim();
};
const getDirectCountries = (item) =>
    [
        item,
        item?.customer,
        item?.serviceProvider,
        item?.supplier,
        item?.globalAgent,
        item?.selectedBookingAgent,
        item?.selectedOriginAgent,
        item?.selectedDestinationAgent,
        item?.selectedNotifyAgent,
    ]
        .filter((value) => value && typeof value === "object")
        .flatMap((value) => [
            value?.customerCountry,
            value?.serviceProviderCountry,
            value?.globalAgentCountry,
            ...(Array.isArray(value?.servicesCountry) ? value.servicesCountry : []),
        ])
        .filter(Boolean);
const getPartyKeys = (item) =>
    [
        item?.customerId,
        item?.customer,
        item?.customerName,
        item?.customer_name,
        item?.serviceProviderId,
        item?.serviceProvider,
        item?.serviceProviderName,
        item?.supplierId,
        item?.supplier,
        item?.supplierName,
        item?.supplier_name,
        item?.globalAgentId,
        item?.globalAgent,
        item?.globalAgentName,
        item?.selectedBookingAgent,
        item?.selectedOriginAgent,
        item?.selectedDestinationAgent,
        item?.selectedNotifyAgent,
    ]
        .map(normalizeLookupKey)
        .filter(Boolean);
const getGeographicScope = (item, lookup = new Map()) => {
    const countries = [
        ...getDirectCountries(item),
        ...getPartyKeys(item).flatMap((key) => lookup.get(key) || []),
    ];
    return countries.some((country) =>
        ["TURKEY", "TURKIYE"].includes(normalizeCountry(country)),
    )
        ? "DOMESTIC"
        : "INTERNATIONAL";
};
const getGeographicLabel = (item, lookup) =>
    getGeographicScope(item, lookup) === "DOMESTIC"
        ? "Domestic"
        : "International";

const getInvoiceNo = (item) =>
    getValue(item, "invoiceNo", "invoice_no") ||
    item?.edm?.invoiceId ||
    item?._id ||
    "N/A";

const getEntityId = (value) => {
    if (!value) return "";
    if (typeof value === "object") {
        return getEntityId(value?._id || value?.id || value?.$oid);
    }
    return String(value).trim();
};

const getFinanceJobKey = (item, index = 0) =>
    getEntityId(
        item?.financeJobId ||
        item?.finance_job_id ||
        item?.jobFinanceId ||
        item?.job_finance_id ||
        item?.shipmentId ||
        item?.shipment_id,
    ) || `invoice-${getEntityId(item?._id || item?.id) || index}`;

const getReturnStatus = (item) =>
    String(item?.returnStatus || item?.return_status || "Draft").trim();

const getOriginalInvoiceId = (item) =>
    getEntityId(
        item?.originalInvoiceId ||
        item?.original_invoice_id ||
        item?.originalInvoice?._id ||
        item?.originalInvoice?.id,
    );

const getOriginalInvoiceNo = (item) =>
    String(
        item?.originalInvoiceNumber ||
        item?.original_invoice_number ||
        item?.originalInvoiceNo ||
        item?.original_invoice_no ||
        item?.originalInvoice?.invoiceNo ||
        item?.originalInvoice?.invoice_no ||
        "",
    ).trim();

const getReturnInvoiceAmount = (item) =>
    Math.abs(
        firstDefinedNumber(
            item?.returnedAmount,
            item?.returned_amount,
            item?.returnAmount,
            item?.return_amount,
            item?.totalAmount,
            item?.total_amount,
            item?.invoiceSalesAmount,
            item?.amount,
        ),
    );

const getLinkedReturnInvoices = (item) =>
    [
        item?.returnInvoices,
        item?.return_invoices,
        item?.linkedReturnInvoices,
        item?.linked_return_invoices,
    ].find(Array.isArray) || [];

const getReturnDate = (item) => {
    const raw =
        item?.returnDate ||
        item?.return_date ||
        item?.invoiceDate ||
        item?.invoice_date ||
        item?.createdAt ||
        item?.created_at ||
        item?.updatedAt ||
        item?.updated_at;
    const date = raw ? new Date(raw) : null;
    return date && !Number.isNaN(date.getTime()) ? date.getTime() : 0;
};

const getReturnSummary = (item) => {
    const allReturns = getLinkedReturnInvoices(item);
    const activeReturns = allReturns.filter(
        (returnInvoice) =>
            normalizeText(getReturnStatus(returnInvoice)) !== "cancelled",
    );
    const issuedReturns = activeReturns.filter(
        (returnInvoice) =>
            normalizeText(getReturnStatus(returnInvoice)) === "issued",
    );
    const latestActiveReturn =
        [...activeReturns].sort((a, b) => getReturnDate(b) - getReturnDate(a))[0] ||
        null;
    const latestReturnInvoice =
        [...allReturns].sort((a, b) => getReturnDate(b) - getReturnDate(a))[0] ||
        null;
    const issuedAmount = issuedReturns.reduce(
        (sum, returnInvoice) => sum + getReturnInvoiceAmount(returnInvoice),
        0,
    );
    const reservedAmount = activeReturns.reduce(
        (sum, returnInvoice) => sum + getReturnInvoiceAmount(returnInvoice),
        0,
    );
    const originalAmount = Math.abs(getInvoiceAmount(item));
    const latestStatus = normalizeText(getReturnStatus(latestActiveReturn));

    let status = "No Return";
    if (latestStatus === "draft") status = "Return Draft";
    else if (latestStatus === "approved") status = "Return Approved";
    else if (
        issuedAmount > 0 &&
        originalAmount > 0 &&
        issuedAmount >= originalAmount - 0.01
    ) {
        status = "Full Return";
    } else if (issuedAmount > 0) status = "Partial Return";
    else if (reservedAmount > 0) status = "Return Pending";
    else if (allReturns.length > 0) status = "Return Cancelled";

    return {
        status,
        issuedAmount,
        reservedAmount,
        count: allReturns.length,
        latestReturnInvoice,
    };
};

const getNetInvoiceAmount = (item) =>
    Math.max(getInvoiceAmount(item) - getReturnSummary(item).issuedAmount, 0);

// Ex-VAT counterpart of getNetInvoiceAmount, used for profit/margin only.
const getExVatNetInvoiceAmount = (item) =>
    Math.max(
        getExVatInvoiceAmount(item) - getReturnSummary(item).issuedAmount,
        0,
    );

const getNetBalanceAmount = (item) =>
    Math.max(getNetInvoiceAmount(item) - getPaidAmount(item), 0);

const getCustomerCreditAmount = (item) =>
    Math.max(getPaidAmount(item) - getNetInvoiceAmount(item), 0);

const getDueDate = (item) => {
    const raw =
        item?.dueDate ||
        item?.due_date ||
        item?.paymentDueDate ||
        item?.payment_due_date ||
        item?.invoiceDueDate ||
        item?.invoice_due_date;
    const date = raw ? new Date(raw) : null;
    return date && !Number.isNaN(date.getTime()) ? date : null;
};

const getDaysOverdue = (item) => {
    const dueDate = getDueDate(item);
    if (!dueDate || getNetBalanceAmount(item) <= 0.01) return 0;
    const today = new Date();
    today.setHours(0, 0, 0, 0);
    dueDate.setHours(0, 0, 0, 0);
    return Math.max(Math.floor((today - dueDate) / 86400000), 0);
};

const RISK_STATUS_OPTIONS = [
    { value: "normal", label: "Normal" },
    { value: "overdue", label: "Overdue" },
    { value: "doubtful", label: "Doubtful" },
    { value: "bad_debt", label: "Bad Debt" },
    { value: "written_off", label: "Written Off" },
];

const RISK_STATUS_STYLES = {
    normal: {
        label: "Normal",
        color: COLORS.green,
        background: COLORS.greenLight,
        border: "#ABEFC6",
    },
    overdue: {
        label: "Overdue",
        color: COLORS.amber,
        background: COLORS.amberLight,
        border: "#FEDF89",
    },
    doubtful: {
        label: "Doubtful",
        color: COLORS.purple,
        background: COLORS.purpleLight,
        border: "#D9D6FE",
    },
    bad_debt: {
        label: "Bad Debt",
        color: COLORS.red,
        background: COLORS.redLight,
        border: "#FECDCA",
    },
    written_off: {
        label: "Written Off",
        color: "#344054",
        background: "#F2F4F7",
        border: "#D0D5DD",
    },
};

const normalizeRiskStatus = (value) => {
    const normalized = normalizeText(value).replace(/[ -]+/g, "_");
    return RISK_STATUS_STYLES[normalized] ? normalized : "normal";
};

const getStoredRiskStatus = (item) =>
    normalizeRiskStatus(
        item?.collectionRisk?.status ||
        item?.collection_risk?.status ||
        item?.riskStatus ||
        item?.risk_status ||
        "normal",
    );

const getRiskStatus = (item) => {
    const stored = getStoredRiskStatus(item);
    if (["doubtful", "bad_debt", "written_off"].includes(stored)) return stored;
    return getDaysOverdue(item) > 0 ? "overdue" : stored;
};

const getRiskField = (item, camel, snake, fallback = 0) =>
    item?.collectionRisk?.[camel] ??
    item?.collection_risk?.[snake] ??
    item?.[camel] ??
    item?.[snake] ??
    fallback;

const getProvisionAmount = (item) =>
    firstDefinedNumber(
        getRiskField(item, "provisionAmount", "provision_amount", 0),
    );
const getExpectedRecoveryAmount = (item) =>
    firstDefinedNumber(
        getRiskField(
            item,
            "expectedRecoveryAmount",
            "expected_recovery_amount",
            getNetBalanceAmount(item),
        ),
    );
const getWrittenOffAmount = (item) =>
    firstDefinedNumber(
        getRiskField(item, "writtenOffAmount", "written_off_amount", 0),
    );

const getCollectionStatus = (item) => {
    const rawStatus = getValue(
        item,
        "collectionStatus",
        "collection_status",
        "Pending",
    );
    const netInvoiceAmount = getNetInvoiceAmount(item);
    const paidAmount = getPaidAmount(item);

    if (paidAmount > netInvoiceAmount + 0.01) return "Credit Due";
    if (netInvoiceAmount <= 0.01 && getReturnSummary(item).issuedAmount > 0) {
        return "Fully Returned";
    }
    if (netInvoiceAmount > 0 && paidAmount >= netInvoiceAmount - 0.01) {
        return "Collected";
    }
    if (paidAmount > 0) return "Partial";
    return rawStatus;
};

const RETURN_STATUS_STYLES = {
    "No Return": { color: "#475467", background: "#F2F4F7", border: "#D0D5DD" },
    "Return Draft": {
        color: "#B54708",
        background: "#FFFAEB",
        border: "#FEDF89",
    },
    "Return Approved": {
        color: "#175CD3",
        background: "#EFF8FF",
        border: "#B2DDFF",
    },
    "Partial Return": {
        color: "#6941C6",
        background: "#F4F3FF",
        border: "#D9D6FE",
    },
    "Full Return": { color: "#B42318", background: "#FEF3F2", border: "#FECDCA" },
    "Return Pending": {
        color: "#B54708",
        background: "#FFFAEB",
        border: "#FEDF89",
    },
    "Return Cancelled": {
        color: "#475467",
        background: "#F2F4F7",
        border: "#D0D5DD",
    },
};

const getPaymentMethod = (item) =>
    getValue(item, "paymentMethod", "payment_method", "Bank Transfer");

const formatCompact = (value, currency = "EUR") =>
    new Intl.NumberFormat("en-US", {
        style: "currency",
        currency,
        notation: "compact",
        maximumFractionDigits: 1,
    }).format(Number(value || 0));

const formatNumber = (value) =>
    new Intl.NumberFormat("en-US", {
        maximumFractionDigits: 0,
    }).format(Number(value || 0));

const normalizeText = (value) =>
    String(value ?? "")
        .trim()
        .toLowerCase()
        .replace(/[ıİ]/g, "i");

const getInvoiceOrigin = (item) =>
    normalizeText(item?.invoiceOrigin || item?.invoice_origin || "tms") ===
    "manual"
        ? "manual"
        : "tms";

const getTransportMode = (item) => {
    if (getInvoiceOrigin(item) === "manual") {
        return "manual";
    }

    const candidates = [
        item?.sourceType,
        item?.source_type,
        item?.transportMode,
        item?.transport_mode,
        item?.shipmentMode,
        item?.shipment_mode,
        item?.productType,
        item?.product_type,
        item?.serviceType,
        item?.service_type,
        item?.tmsSource,
        item?.tms_source,
        item?.module,
        item?.source,
        getShipmentNo(item),
    ]
        .map(normalizeText)
        .join(" ");

    if (
        candidates.includes("ocean") ||
        candidates.includes("sea") ||
        candidates.includes("fcl") ||
        candidates.includes("lcl") ||
        candidates.includes("ohfl") ||
        candidates.includes("ombl")
    ) {
        return "ocean";
    }

    if (
        candidates.includes("air") ||
        candidates.includes("flight") ||
        candidates.includes("ahfl") ||
        candidates.includes("ambl")
    ) {
        return "air";
    }

    return "road";
};

const getShipmentType = (item) => {
    const raw =
        item?.shipmentType ||
        item?.shipment_type ||
        item?.transportType ||
        item?.transport_type ||
        item?.oceanTransportType ||
        item?.ocean_transport_type ||
        item?.airTransportType ||
        item?.air_transport_type ||
        item?.serviceType ||
        item?.service_type ||
        "General";

    return String(raw).trim() || "General";
};

const MODE_CONFIG = {
    ocean: {
        label: "Ocean",
        color: "#1570EF",
        light: "#EFF8FF",
        border: "#B2DDFF",
        icon: DirectionsBoat,
    },
    road: {
        label: "Road",
        color: "#B54708",
        light: "#FFFAEB",
        border: "#FEDF89",
        icon: LocalShipping,
    },
    air: {
        label: "Air",
        color: "#6941C6",
        light: "#F4F3FF",
        border: "#D9D6FE",
        icon: Flight,
    },
    manual: {
        label: "Manual",
        color: "#475467",
        light: "#F2F4F7",
        border: "#D0D5DD",
        icon: ReceiptLong,
    },
};

const TransportCollectionCard = ({
                                     mode,
                                     data,
                                     totalRecords,
                                     currency = "EUR",
                                 }) => {
    const config = MODE_CONFIG[mode] || MODE_CONFIG.manual;
    const Icon = config.icon;
    const share = totalRecords ? (data.count / totalRecords) * 100 : 0;

    return (
        <Card sx={{ ...cardSx, height: "100%" }}>
            <MDBox p={2.4}>
                <MDBox
                    display="flex"
                    justifyContent="space-between"
                    alignItems="flex-start"
                >
                    <MDBox>
                        <MDTypography
                            variant="caption"
                            fontWeight="bold"
                            sx={{ color: COLORS.subtext, letterSpacing: ".08em" }}
                        >
                            {`${financeText(config.label).toUpperCase()} ${financeText("COLLECTIONS")}`}
                        </MDTypography>
                        <MDTypography
                            variant="h4"
                            fontWeight="bold"
                            mt={0.8}
                            sx={{ color: COLORS.text }}
                        >
                            {formatNumber(data.count)}
                        </MDTypography>
                        <MDTypography variant="caption" sx={{ color: COLORS.subtext }}>
                            {financeText("Payment records")} · {share.toFixed(1)}% {financeText("share")}
                        </MDTypography>
                    </MDBox>
                    <MDBox
                        width={46}
                        height={46}
                        borderRadius="8px"
                        display="flex"
                        alignItems="center"
                        justifyContent="center"
                        sx={{
                            color: config.color,
                            background: config.light,
                            border: `1px solid ${config.border}`,
                        }}
                    >
                        <Icon />
                    </MDBox>
                </MDBox>

                <MDBox mt={2}>
                    <MDBox display="flex" justifyContent="space-between" mb={0.6}>
                        <MDTypography variant="caption" sx={{ color: COLORS.subtext }}>
                            {financeText("Matched rate")}
                        </MDTypography>
                        <MDTypography
                            variant="caption"
                            fontWeight="bold"
                            sx={{ color: config.color }}
                        >
                            {data.matchedRate.toFixed(1)}%
                        </MDTypography>
                    </MDBox>
                    <LinearProgress
                        variant="determinate"
                        value={Math.min(100, Math.max(0, data.matchedRate))}
                        sx={{
                            height: 7,
                            borderRadius: "999px",
                            backgroundColor: "#EEF2F6",
                            "& .MuiLinearProgress-bar": {
                                borderRadius: "999px",
                                backgroundColor: config.color,
                            },
                        }}
                    />
                </MDBox>

                <Grid container spacing={1.2} mt={0.8}>
                    <Grid item xs={4}>
                        <MDTypography
                            variant="caption"
                            display="block"
                            sx={{ color: COLORS.subtext }}
                        >
                            {financeText("Collected")}
                        </MDTypography>
                        <MDTypography
                            variant="button"
                            fontWeight="bold"
                            sx={{ color: COLORS.green }}
                        >
                            {formatCompact(data.collected, currency)}
                        </MDTypography>
                    </Grid>
                    <Grid item xs={4}>
                        <MDTypography
                            variant="caption"
                            display="block"
                            sx={{ color: COLORS.subtext }}
                        >
                            {financeText("Pending")}
                        </MDTypography>
                        <MDTypography
                            variant="button"
                            fontWeight="bold"
                            sx={{ color: COLORS.amber }}
                        >
                            {formatCompact(data.pending, currency)}
                        </MDTypography>
                    </Grid>
                    <Grid item xs={4}>
                        <MDTypography
                            variant="caption"
                            display="block"
                            sx={{ color: COLORS.subtext }}
                        >
                            {financeText("Invoice value")}
                        </MDTypography>
                        <MDTypography
                            variant="button"
                            fontWeight="bold"
                            sx={{ color: config.color }}
                        >
                            {formatCompact(data.invoiceTotal, currency)}
                        </MDTypography>
                    </Grid>
                </Grid>

                <Divider sx={{ my: 1.5 }} />
                <MDBox
                    display="flex"
                    justifyContent="space-between"
                    alignItems="center"
                >
                    <MDTypography variant="caption" sx={{ color: COLORS.subtext }}>
                        {financeText("Gross profit")}
                    </MDTypography>
                    <MDTypography
                        variant="button"
                        fontWeight="bold"
                        sx={{ color: data.profit >= 0 ? COLORS.green : COLORS.red }}
                    >
                        {formatCompact(data.profit, currency)} · {data.margin.toFixed(1)}%
                    </MDTypography>
                </MDBox>
            </MDBox>
        </Card>
    );
};

const ModeCollectionsChart = ({ summary, currency = "EUR" }) => {
    const max = Math.max(
        ...Object.values(summary).map((item) => item.collected),
        1,
    );
    return (
        <MDBox>
            {Object.entries(summary).map(([mode, item]) => {
                const config = MODE_CONFIG[mode] || MODE_CONFIG.manual;
                return (
                    <MDBox key={mode} mb={2.2}>
                        <MDBox
                            display="flex"
                            justifyContent="space-between"
                            alignItems="center"
                            mb={0.8}
                        >
                            <MDBox display="flex" alignItems="center" gap={1}>
                                <MDBox
                                    width={10}
                                    height={10}
                                    borderRadius="50%"
                                    sx={{ background: config.color }}
                                />
                                <MDTypography
                                    variant="button"
                                    fontWeight="bold"
                                    sx={{ color: COLORS.text }}
                                >
                                    {financeText(config.label)}
                                </MDTypography>
                            </MDBox>
                            <MDBox textAlign="right">
                                <MDTypography
                                    variant="button"
                                    fontWeight="bold"
                                    display="block"
                                    sx={{ color: config.color }}
                                >
                                    {formatCompact(item.collected, currency)}
                                </MDTypography>
                                <MDTypography variant="caption" sx={{ color: COLORS.subtext }}>
                                    {financeText("Pending")} {formatCompact(item.pending, currency)}
                                </MDTypography>
                            </MDBox>
                        </MDBox>
                        <MDBox
                            height={12}
                            borderRadius="999px"
                            sx={{ background: "#EEF2F6", overflow: "hidden" }}
                        >
                            <MDBox
                                height="100%"
                                borderRadius="999px"
                                sx={{
                                    width: `${(item.collected / max) * 100}%`,
                                    minWidth: item.collected ? 8 : 0,
                                    background: config.color,
                                }}
                            />
                        </MDBox>
                    </MDBox>
                );
            })}
        </MDBox>
    );
};

const Collections = () => {
    const navigate = useNavigate();
    const location = useLocation();

    const [financeLanguage, setFinanceLanguageState] = useState(getFinanceLanguage);

    const changeFinanceLanguage = (event) => {
        const nextLanguage = event.target.value === "tr" ? "tr" : "en";
        saveFinanceLanguage(nextLanguage);
        setFinanceLanguageState(nextLanguage);
    };

    const [collections, setCollections] = useState([]);
    const [businessPartners, setBusinessPartners] = useState([]);
    const [loading, setLoading] = useState(true);
    const [lastUpdated, setLastUpdated] = useState(new Date());

    const [search, setSearch] = useState("");
    const [collectionStatus, setCollectionStatus] = useState("all");
    const [paymentMethod, setPaymentMethod] = useState("all");
    const currentDate = new Date();
    const currentYear = currentDate.getFullYear();
    const todayValue = `${currentYear}-${String(currentDate.getMonth() + 1).padStart(2, "0")}-${String(currentDate.getDate()).padStart(2, "0")}`;
    const [startDate, setStartDate] = useState(`${currentYear}-01-01`);
    const [endDate, setEndDate] = useState(todayValue);
    const [selectedParty, setSelectedParty] = useState(null);
    const [transportMode, setTransportMode] = useState("all");
    const [invoiceOrigin, setInvoiceOrigin] = useState("all");
    const [currencyFilter, setCurrencyFilter] = useState("all");
    const [geographicScope, setGeographicScope] = useState("all");
    const [returnStatus, setReturnStatus] = useState("all");
    const [riskStatus, setRiskStatus] = useState("all");
    const [overdueRange, setOverdueRange] = useState("all");
    const [exchangeRates, setExchangeRates] = useState(null);
    // Display-only currency: which currency the page's AGGREGATED figures are
    // shown in. Independent of `currencyFilter` above (that one filters the
    // record list by each record's own currency and is left untouched).
    const [selectedCurrency, setSelectedCurrency] = useState("EUR");

    const [modalOpen, setModalOpen] = useState(false);
    const [selectedInvoice, setSelectedInvoice] = useState(null);
    const [riskModalOpen, setRiskModalOpen] = useState(false);
    const [riskSaving, setRiskSaving] = useState(false);
    const [riskForm, setRiskForm] = useState({
        status: "normal",
        reason: "",
        expectedRecoveryAmount: "",
        provisionAmount: "",
        writtenOffAmount: "",
        notes: "",
    });

    const [collectionForm, setCollectionForm] = useState({
        amount: "",
        collectionDate: toLocalDateInputValue(),
        paymentMethod: "Bank Transfer",
        bankName: "",
        referenceNo: "",
        note: "",
    });

    const token = localStorage.getItem("token");
    // Every aggregated amount on this page (KPI cards, hero stat, mode
    // chart, chips, CSV export) is converted into this currency. It follows
    // the user's explicit "Display Currency" choice rather than the record
    // filter: previously it was derived from `currencyFilter`, which meant
    // the reporting currency silently changed whenever someone narrowed the
    // LIST to one currency, and there was no way to view the totals in a
    // currency other than the one being filtered on.
    const reportingCurrency = normalizeCurrencyCode(selectedCurrency);

    const fetchWithAuth = useCallback(
        async (path) => {
            const response = await fetch(`${API_URL}${path}`, {
                headers: {
                    Authorization: `Bearer ${token}`,
                },
            });

            if (!response.ok) {
                throw new Error(`${path} fetch failed`);
            }

            const data = await response.json();
            const candidates = [
                data,
                data?.data,
                data?.collections,
                data?.invoices,
                data?.returnInvoices,
                data?.return_invoices,
                data?.jobs,
                data?.results,
                data?.records,
                data?.data?.data,
                data?.data?.collections,
                data?.data?.invoices,
                data?.data?.returnInvoices,
                data?.data?.return_invoices,
                data?.data?.jobs,
                data?.data?.results,
                data?.data?.records,
            ];

            return candidates.find(Array.isArray) || [];
        },
        [token],
    );

    useEffect(() => {
        const loadExchangeRates = async () => {
            try {
                const response = await fetch(
                    "https://api.exchangerate-api.com/v4/latest/USD",
                );
                if (!response.ok) throw new Error("Exchange rates could not be loaded");
                const data = await response.json();
                setExchangeRates(data?.rates || null);
            } catch (error) {
                console.error("Exchange rate fetch error:", error);
                setExchangeRates(null);
            }
        };

        loadExchangeRates();
    }, []);

    const convertCurrency = useCallback(
        (amount, fromCurrency, toCurrency) => {
            const value = Number(amount) || 0;
            const from = normalizeCurrencyCode(fromCurrency);
            const to = normalizeCurrencyCode(toCurrency);

            if (from === to) return value;
            if (!exchangeRates?.[from] || !exchangeRates?.[to]) return null;

            return (value / exchangeRates[from]) * exchangeRates[to];
        },
        [exchangeRates],
    );

    const getConvertedFinancials = useCallback(
        (item, targetCurrency = reportingCurrency) => {
            const invoice = convertCurrency(
                getExVatNetInvoiceAmount(item),
                getCurrency(item),
                targetCurrency,
            );
            const cost = convertCurrency(
                getTotalCost(item),
                getCostCurrency(item),
                targetCurrency,
            );

            if (invoice === null || cost === null) {
                return { invoice: null, cost: null, profit: null, margin: null };
            }

            const profit = invoice - cost;
            return {
                invoice,
                cost,
                profit,
                margin: invoice > 0 ? (profit / invoice) * 100 : 0,
            };
        },
        [convertCurrency, reportingCurrency],
    );

    const fetchCollections = useCallback(async () => {
        try {
            setLoading(true);

            const [collectionData, manualInvoices, returnInvoices] =
                await Promise.all([
                    fetchWithAuth("/finance/collections"),
                    fetchWithAuth(
                        "/finance/manual-invoices?invoiceType=sales&limit=200",
                    ).catch((manualInvoiceError) => {
                        console.warn(
                            "Manual invoices could not be loaded:",
                            manualInvoiceError,
                        );
                        return [];
                    }),
                    fetchWithAuth("/finance/return-invoices?invoiceType=sales").catch(
                        (returnInvoiceError) => {
                            console.warn(
                                "Return invoices could not be loaded:",
                                returnInvoiceError,
                            );
                            return [];
                        },
                    ),
                ]);
            const uniqueRecords = new Map();

            [
                ...manualInvoices.filter(
                    (invoice) =>
                        getPaidAmount(invoice) > 0 ||
                        ["partial", "collected"].includes(
                            String(
                                invoice?.collectionStatus || invoice?.collection_status || "",
                            ).toLowerCase(),
                        ),
                ),
                ...collectionData,
            ].forEach((record, index) => {
                const key =
                    record?._id ||
                    record?.id ||
                    record?.invoiceNo ||
                    record?.invoice_no ||
                    `collection-${index}`;
                uniqueRecords.set(String(key), record);
            });

            const uniqueReturnInvoices = Array.from(
                new Map(
                    returnInvoices.map((returnInvoice, index) => [
                        getEntityId(returnInvoice?._id || returnInvoice?.id) ||
                        `return-${index}`,
                        returnInvoice,
                    ]),
                ).values(),
            );
            const returnsByOriginalId = new Map();
            const returnsByOriginalNo = new Map();

            uniqueReturnInvoices.forEach((returnInvoice) => {
                const originalInvoiceId = getOriginalInvoiceId(returnInvoice);
                const originalInvoiceNo = normalizeLookupKey(
                    getOriginalInvoiceNo(returnInvoice),
                );

                if (originalInvoiceId) {
                    returnsByOriginalId.set(originalInvoiceId, [
                        ...(returnsByOriginalId.get(originalInvoiceId) || []),
                        returnInvoice,
                    ]);
                }
                if (originalInvoiceNo) {
                    returnsByOriginalNo.set(originalInvoiceNo, [
                        ...(returnsByOriginalNo.get(originalInvoiceNo) || []),
                        returnInvoice,
                    ]);
                }
            });

            const enrichedCollections = Array.from(uniqueRecords.values()).map(
                (record) => {
                    const identityKeys = [
                        record?._id,
                        record?.id,
                        record?.invoiceId,
                        record?.invoice_id,
                        record?.customerInvoiceId,
                        record?.customer_invoice_id,
                    ]
                        .map(getEntityId)
                        .filter(Boolean);
                    const invoiceNoKey = normalizeLookupKey(getInvoiceNo(record));
                    const linkedReturns = [
                        ...getLinkedReturnInvoices(record),
                        ...identityKeys.flatMap(
                            (identityKey) => returnsByOriginalId.get(identityKey) || [],
                        ),
                        ...(returnsByOriginalNo.get(invoiceNoKey) || []),
                    ];
                    const uniqueLinkedReturns = Array.from(
                        new Map(
                            linkedReturns.map((returnInvoice, index) => [
                                getEntityId(returnInvoice?._id || returnInvoice?.id) ||
                                `${invoiceNoKey}-return-${index}`,
                                returnInvoice,
                            ]),
                        ).values(),
                    );

                    return {
                        ...record,
                        returnInvoices: uniqueLinkedReturns,
                    };
                },
            );

            setCollections(enrichedCollections);
            setLastUpdated(new Date());
        } catch (error) {
            console.error("Collections fetch error:", error);
            setCollections([]);
        } finally {
            setLoading(false);
        }
    }, [fetchWithAuth]);

    const fetchBusinessPartners = useCallback(async () => {
        const firstAvailable = async (paths) => {
            for (const path of paths) {
                try {
                    return await fetchWithAuth(path);
                } catch (error) {
                    console.warn(`${path} lookup failed`, error);
                }
            }
            return [];
        };
        const [customers, providers, agents] = await Promise.all([
            firstAvailable(["/customer", "/customers"]),
            firstAvailable(["/serviceProvider", "/service-provider"]),
            firstAvailable(["/globalAgent", "/global-agent"]),
        ]);
        setBusinessPartners([...customers, ...providers, ...agents]);
    }, [fetchWithAuth]);

    useEffect(() => {
        fetchCollections();
        fetchBusinessPartners();
    }, [fetchCollections, fetchBusinessPartners]);

    const partyCountryLookup = useMemo(() => {
        const lookup = new Map();
        businessPartners.forEach((record) => {
            const item = record?.attributes || record;
            const countries = [
                item?.customerCountry,
                item?.serviceProviderCountry,
                item?.globalAgentCountry,
                ...(Array.isArray(item?.servicesCountry) ? item.servicesCountry : []),
            ].filter(Boolean);
            [
                record?.id,
                item?._id,
                item?.id,
                item?.name,
                item?.nameKey,
                item?.companyName,
                item?.customerNo,
                item?.serviceProviderNo,
                item?.globalAgentNo,
            ]
                .map(normalizeLookupKey)
                .filter(Boolean)
                .forEach((key) =>
                    lookup.set(key, [...(lookup.get(key) || []), ...countries]),
                );
        });
        return lookup;
    }, [businessPartners]);

    useEffect(() => {
        if (location.state?.openCollectionModal && location.state?.invoice) {
            const invoice = location.state.invoice;

            setSelectedInvoice(invoice);
            setCollectionForm({
                amount: String(getNetBalanceAmount(invoice) || ""),
                collectionDate: toLocalDateInputValue(),
                paymentMethod: "Bank Transfer",
                bankName: "",
                referenceNo: getInvoiceNo(invoice),
                note: "",
            });

            setModalOpen(true);
            window.history.replaceState({}, document.title);
        }
    }, [location.state]);

    const statusOptions = useMemo(() => {
        const statuses = collections
            .map((item) => String(getCollectionStatus(item)).trim())
            .filter(Boolean);

        const methods = collections
            .map((item) => String(getPaymentMethod(item)).trim())
            .filter(Boolean);

        return {
            statuses: ["all", ...Array.from(new Set(statuses))],
            methods: ["all", ...Array.from(new Set(methods))],
        };
    }, [collections]);

    const partyOptions = useMemo(() => {
        const unique = new Map();

        collections.forEach((item) => {
            const customerName = getCustomerName(item);
            const supplierName = getSupplierName(item);

            if (customerName && customerName !== "N/A") {
                unique.set(`CUSTOMER-${customerName}`, {
                    type: "CUSTOMER",
                    name: customerName,
                    label: `Customer - ${customerName}`,
                });
            }

            if (supplierName && supplierName !== "N/A") {
                unique.set(`SUPPLIER-${supplierName}`, {
                    type: "SUPPLIER",
                    name: supplierName,
                    label: `Supplier - ${supplierName}`,
                });
            }
        });

        return Array.from(unique.values()).sort((a, b) =>
            a.label.localeCompare(b.label),
        );
    }, [collections]);

    const transportModeOptions = useMemo(
        () => Array.from(new Set(collections.map(getTransportMode))).sort(),
        [collections],
    );

    const currencyOptions = useMemo(
        () =>
            Array.from(
                new Set(
                    collections.map((item) => String(getCurrency(item)).toUpperCase()),
                ),
            ).sort(),
        [collections],
    );

    const returnStatusOptions = useMemo(
        () => [
            "all",
            ...Array.from(
                new Set(collections.map((item) => getReturnSummary(item).status)),
            ),
        ],
        [collections],
    );

    const filteredCollections = useMemo(() => {
        const searchText = search.toLowerCase().trim();
        const rangeStart = startDate ? new Date(`${startDate}T00:00:00`) : null;
        const rangeEnd = endDate ? new Date(`${endDate}T23:59:59`) : null;

        return collections.filter((collection) => {
            const jobNo = String(getShipmentNo(collection)).toLowerCase();

            const customer = String(getCustomerName(collection)).toLowerCase();
            const supplier = String(getSupplierName(collection)).toLowerCase();
            const collectionDate = getDateValue(collection);

            const invoiceNo = String(getInvoiceNo(collection)).toLowerCase();

            const refNo = String(
                getValue(collection, "referenceNo", "reference_no", ""),
            ).toLowerCase();

            const currentStatus = String(
                getCollectionStatus(collection),
            ).toLowerCase();

            const currentMethod = String(getPaymentMethod(collection)).toLowerCase();
            const currentReturnStatus = normalizeText(
                getReturnSummary(collection).status,
            );
            const currentRiskStatus = getRiskStatus(collection);
            const daysOverdue = getDaysOverdue(collection);
            const returnDocumentText = getLinkedReturnInvoices(collection)
                .map((returnInvoice) =>
                    [
                        returnInvoice?.returnInvoiceNo,
                        returnInvoice?.return_invoice_no,
                        returnInvoice?.invoiceNo,
                        returnInvoice?.invoice_no,
                    ]
                        .filter(Boolean)
                        .join(" "),
                )
                .join(" ")
                .toLowerCase();

            const matchesSearch =
                !searchText ||
                jobNo.includes(searchText) ||
                customer.includes(searchText) ||
                supplier.includes(searchText) ||
                invoiceNo.includes(searchText) ||
                refNo.includes(searchText) ||
                returnDocumentText.includes(searchText);

            const matchesStartDate =
                !rangeStart || (collectionDate && collectionDate >= rangeStart);
            const matchesEndDate =
                !rangeEnd || (collectionDate && collectionDate <= rangeEnd);
            const matchesParty =
                !selectedParty ||
                (selectedParty.type === "CUSTOMER"
                    ? getCustomerName(collection) === selectedParty.name
                    : getSupplierName(collection) === selectedParty.name);
            const matchesTransportMode =
                transportMode === "all" ||
                getTransportMode(collection) === transportMode;
            const matchesInvoiceOrigin =
                invoiceOrigin === "all" ||
                getInvoiceOrigin(collection) === invoiceOrigin;
            const matchesCurrency =
                currencyFilter === "all" ||
                String(getCurrency(collection)).toUpperCase() === currencyFilter;
            const matchesGeographicScope =
                geographicScope === "all" ||
                getGeographicScope(collection, partyCountryLookup) === geographicScope;
            const matchesReturnStatus =
                returnStatus === "all" ||
                currentReturnStatus === normalizeText(returnStatus);
            const matchesRiskStatus =
                riskStatus === "all" || currentRiskStatus === riskStatus;
            const matchesOverdueRange =
                overdueRange === "all" ||
                (overdueRange === "current" && daysOverdue === 0) ||
                (overdueRange === "1-30" && daysOverdue >= 1 && daysOverdue <= 30) ||
                (overdueRange === "31-60" && daysOverdue >= 31 && daysOverdue <= 60) ||
                (overdueRange === "61-90" && daysOverdue >= 61 && daysOverdue <= 90) ||
                (overdueRange === "90+" && daysOverdue > 90);

            const matchesStatus =
                collectionStatus === "all" ||
                currentStatus === collectionStatus.toLowerCase();

            const matchesMethod =
                paymentMethod === "all" ||
                currentMethod === paymentMethod.toLowerCase();

            return (
                matchesSearch &&
                matchesStartDate &&
                matchesEndDate &&
                matchesParty &&
                matchesTransportMode &&
                matchesInvoiceOrigin &&
                matchesCurrency &&
                matchesGeographicScope &&
                matchesReturnStatus &&
                matchesRiskStatus &&
                matchesOverdueRange &&
                matchesStatus &&
                matchesMethod
            );
        });
    }, [
        collections,
        search,
        collectionStatus,
        paymentMethod,
        startDate,
        endDate,
        selectedParty,
        transportMode,
        invoiceOrigin,
        currencyFilter,
        geographicScope,
        returnStatus,
        riskStatus,
        overdueRange,
        partyCountryLookup,
    ]);

    const stats = useMemo(() => {
        const countedCostJobs = new Set();
        const countedOperationalCostJobs = new Set();
        const countedBuyingRateJobs = new Set();
        const totalCollected = filteredCollections.reduce(
            (sum, item) =>
                sum +
                (convertCurrency(
                    getAmount(item),
                    getCurrency(item),
                    reportingCurrency,
                ) ?? 0),
            0,
        );

        const pendingAmount = filteredCollections.reduce(
            (sum, item) =>
                sum +
                (convertCurrency(
                    getNetBalanceAmount(item),
                    getCurrency(item),
                    reportingCurrency,
                ) ?? 0),
            0,
        );

        const returnedAmount = filteredCollections.reduce(
            (sum, item) =>
                sum +
                (convertCurrency(
                    getReturnSummary(item).issuedAmount,
                    getCurrency(item),
                    reportingCurrency,
                ) ?? 0),
            0,
        );

        const customerCreditAmount = filteredCollections.reduce(
            (sum, item) =>
                sum +
                (convertCurrency(
                    getCustomerCreditAmount(item),
                    getCurrency(item),
                    reportingCurrency,
                ) ?? 0),
            0,
        );

        const matchedCount = filteredCollections.filter((item) =>
            ["matched", "collected", "partial"].some((key) =>
                getCollectionStatus(item).toLowerCase().includes(key),
            ),
        ).length;

        const failedCount = filteredCollections.filter((item) =>
            ["failed", "cancelled"].some((key) =>
                getCollectionStatus(item).toLowerCase().includes(key),
            ),
        ).length;

        const matchedRate = filteredCollections.length
            ? (matchedCount / filteredCollections.length) * 100
            : 0;

        const invoiceTotal = filteredCollections.reduce(
            (sum, item) => sum + (getConvertedFinancials(item).invoice ?? 0),
            0,
        );

        // Billed (VAT-inclusive where applicable) total shown as "Invoice Total".
        const billedTotal = filteredCollections.reduce(
            (sum, item) =>
                sum +
                (convertCurrency(
                    getNetInvoiceAmount(item),
                    getCurrency(item),
                    reportingCurrency,
                ) ?? 0),
            0,
        );

        const totalCost = filteredCollections.reduce((sum, item, index) => {
            const jobKey = getFinanceJobKey(item, index);
            if (countedCostJobs.has(jobKey)) return sum;
            countedCostJobs.add(jobKey);
            return sum + (getConvertedFinancials(item).cost ?? 0);
        }, 0);

        const operationalCost = filteredCollections.reduce((sum, item, index) => {
            const jobKey = getFinanceJobKey(item, index);
            if (countedOperationalCostJobs.has(jobKey)) return sum;
            countedOperationalCostJobs.add(jobKey);
            return (
                sum +
                (convertCurrency(
                    getOperationalCost(item),
                    getCostCurrency(item),
                    reportingCurrency,
                ) ?? 0)
            );
        }, 0);

        const buyingRate = filteredCollections.reduce((sum, item, index) => {
            const jobKey = getFinanceJobKey(item, index);
            if (countedBuyingRateJobs.has(jobKey)) return sum;
            countedBuyingRateJobs.add(jobKey);
            return (
                sum +
                (convertCurrency(
                    getBuyingRate(item),
                    getCostCurrency(item),
                    reportingCurrency,
                ) ?? 0)
            );
        }, 0);

        const overdueAmount = filteredCollections.reduce(
            (sum, item) =>
                getDaysOverdue(item) > 0
                    ? sum +
                    (convertCurrency(
                        getNetBalanceAmount(item),
                        getCurrency(item),
                        reportingCurrency,
                    ) ?? 0)
                    : sum,
            0,
        );
        const doubtfulAmount = filteredCollections.reduce(
            (sum, item) =>
                getRiskStatus(item) === "doubtful"
                    ? sum +
                    (convertCurrency(
                        getNetBalanceAmount(item),
                        getCurrency(item),
                        reportingCurrency,
                    ) ?? 0)
                    : sum,
            0,
        );
        const badDebtAmount = filteredCollections.reduce(
            (sum, item) =>
                getRiskStatus(item) === "bad_debt"
                    ? sum +
                    (convertCurrency(
                        getNetBalanceAmount(item),
                        getCurrency(item),
                        reportingCurrency,
                    ) ?? 0)
                    : sum,
            0,
        );
        const writtenOffAmount = filteredCollections.reduce(
            (sum, item) =>
                sum +
                (convertCurrency(
                    getWrittenOffAmount(item),
                    getCurrency(item),
                    reportingCurrency,
                ) ?? 0),
            0,
        );
        const provisionAmount = filteredCollections.reduce(
            (sum, item) =>
                sum +
                (convertCurrency(
                    getProvisionAmount(item),
                    getCurrency(item),
                    reportingCurrency,
                ) ?? 0),
            0,
        );

        const profit = invoiceTotal - totalCost;
        const margin = invoiceTotal ? (profit / invoiceTotal) * 100 : 0;

        return {
            totalCollected,
            pendingAmount,
            returnedAmount,
            customerCreditAmount,
            matchedCount,
            failedCount,
            count: filteredCollections.length,
            matchedRate,
            invoiceTotal,
            billedTotal,
            totalCost,
            operationalCost,
            buyingRate,
            overdueAmount,
            doubtfulAmount,
            badDebtAmount,
            writtenOffAmount,
            provisionAmount,
            profit,
            margin,
        };
    }, [
        filteredCollections,
        getConvertedFinancials,
        convertCurrency,
        reportingCurrency,
    ]);

    const transportSummary = useMemo(() => {
        const initial = {
            ocean: {
                count: 0,
                collected: 0,
                pending: 0,
                invoiceTotal: 0,
                cost: 0,
                profit: 0,
                margin: 0,
                matched: 0,
                matchedRate: 0,
            },
            road: {
                count: 0,
                collected: 0,
                pending: 0,
                invoiceTotal: 0,
                cost: 0,
                profit: 0,
                margin: 0,
                matched: 0,
                matchedRate: 0,
            },
            air: {
                count: 0,
                collected: 0,
                pending: 0,
                invoiceTotal: 0,
                cost: 0,
                profit: 0,
                margin: 0,
                matched: 0,
                matchedRate: 0,
            },
            manual: {
                count: 0,
                collected: 0,
                pending: 0,
                invoiceTotal: 0,
                cost: 0,
                profit: 0,
                margin: 0,
                matched: 0,
                matchedRate: 0,
            },
        };

        const countedCostJobs = new Set();

        filteredCollections.forEach((item, index) => {
            const mode = getTransportMode(item);
            const financials = getConvertedFinancials(item);
            const amount =
                convertCurrency(
                    getAmount(item),
                    getCurrency(item),
                    reportingCurrency,
                ) ?? 0;
            const netBalance =
                convertCurrency(
                    getNetBalanceAmount(item),
                    getCurrency(item),
                    reportingCurrency,
                ) ?? 0;
            const invoiceTotal = financials.invoice ?? 0;
            const jobKey = getFinanceJobKey(item, index);
            const shouldCountCost = !countedCostJobs.has(jobKey);
            const cost = shouldCountCost ? (financials.cost ?? 0) : 0;
            if (shouldCountCost) countedCostJobs.add(jobKey);
            const status = getCollectionStatus(item).toLowerCase();
            const isPending = status.includes("pending");
            const isMatched = ["matched", "collected", "partial"].some((key) =>
                status.includes(key),
            );

            const target = initial[mode] || initial.manual;

            target.count += 1;
            target.collected += isPending ? 0 : amount;
            target.pending += netBalance;
            target.invoiceTotal += invoiceTotal;
            target.cost += cost;
            target.profit += invoiceTotal - cost;
            if (isMatched) target.matched += 1;
        });

        Object.values(initial).forEach((item) => {
            item.margin = item.invoiceTotal
                ? (item.profit / item.invoiceTotal) * 100
                : 0;
            item.matchedRate = item.count ? (item.matched / item.count) * 100 : 0;
        });

        return initial;
    }, [
        filteredCollections,
        getConvertedFinancials,
        convertCurrency,
        reportingCurrency,
    ]);

    const shipmentTypeSummary = useMemo(() => {
        const counts = {};
        filteredCollections.forEach((item) => {
            const type = getShipmentType(item);
            counts[type] = (counts[type] || 0) + 1;
        });
        return Object.entries(counts)
            .sort((a, b) => b[1] - a[1])
            .slice(0, 8);
    }, [filteredCollections]);

    const openLatestReturnInvoice = (item) => {
        const latestReturnInvoice = getReturnSummary(item).latestReturnInvoice;
        const returnInvoiceId = getEntityId(
            latestReturnInvoice?._id || latestReturnInvoice?.id,
        );

        if (!returnInvoiceId) {
            appAlert(financeText("No linked return invoice was found."));
            return;
        }

        navigate(`/invoice/finance/return-invoices/${returnInvoiceId}`, {
            state: {
                returnInvoice: latestReturnInvoice,
                originalInvoice: item,
                originalInvoiceId: getOriginalInvoiceId(latestReturnInvoice),
            },
        });
    };

    const openInvoiceDetails = (item) => {
        const invoiceId =
            item?._id || item?.id || item?.invoiceId || item?.invoice_id;

        if (getInvoiceOrigin(item) === "manual") {
            if (!invoiceId) {
                appAlert(financeText("Manual Invoice ID not found!"));
                return;
            }

            navigate(`/invoice/finance/jobs/${invoiceId}`, {
                state: {
                    job: item,
                    financeJobId: invoiceId,
                    invoiceOrigin: "manual",
                },
            });
            return;
        }

        const financeJobId =
            item?.financeJobId ||
            item?.finance_job_id ||
            item?.jobFinanceId ||
            item?.job_finance_id ||
            invoiceId;

        if (!financeJobId) {
            appAlert(financeText("Finance Job ID not found!"));
            return;
        }

        navigate(`/invoice/finance/jobs/${financeJobId}`, {
            state: {
                job: item,
                financeJobId,
            },
        });
    };

    const openInvoiceEditor = async (item) => {
        const invoiceId =
            item?._id || item?.id || item?.invoiceId || item?.invoice_id;

        if (getInvoiceOrigin(item) === "manual") {
            if (!invoiceId) {
                appAlert(financeText("Manual Invoice ID not found!"));
                return;
            }

            navigate(`/invoice/finance/manual-invoices/${invoiceId}/edit`, {
                state: {
                    invoice: item,
                    invoiceId,
                    editMode: true,
                },
            });
            return;
        }

        let resolvedItem = item;

        if (invoiceId) {
            const detailPaths = [
                `/finance/manual-invoices/${invoiceId}`,
                `/invoice/${invoiceId}`,
                `/finance/customer-invoices/${invoiceId}`,
                `/finance/jobs/${invoiceId}`,
            ];

            for (const path of detailPaths) {
                try {
                    const response = await fetch(`${API_URL}${path}`, {
                        headers: {
                            Authorization: `Bearer ${token}`,
                        },
                    });
                    if (!response.ok) continue;

                    const payload = await response.json();
                    const detail =
                        payload?.data?.invoice ||
                        payload?.data?.job ||
                        payload?.data?.data ||
                        payload?.invoice ||
                        payload?.job ||
                        payload?.data ||
                        payload;

                    if (detail && typeof detail === "object") {
                        resolvedItem = {
                            ...item,
                            ...detail,
                            invoiceItems:
                                detail?.invoiceItems ||
                                detail?.invoice_items ||
                                item?.invoiceItems ||
                                item?.invoice_items ||
                                [],
                        };
                        break;
                    }
                } catch (error) {
                    console.warn(`${path} invoice detail lookup failed`, error);
                }
            }
        }

        const shipmentId =
            resolvedItem?.shipmentId ||
            resolvedItem?.shipment_id ||
            resolvedItem?._id ||
            resolvedItem?.id;

        if (!shipmentId) {
            appAlert(financeText("Shipment ID not found!"));
            return;
        }

        const sellingCurrency = getCurrency(resolvedItem);
        const costCurrency = getCostCurrency(resolvedItem);
        const sourceItems =
            [
                resolvedItem?.invoiceItems,
                resolvedItem?.invoice_items,
                resolvedItem?.cargoDetails,
                resolvedItem?.cargo_details,
            ].find(Array.isArray) || [];
        const normalizedItems = sourceItems.map((invoiceItem) => {
            const rawItem = invoiceItem?.attributes || invoiceItem;
            const unitQuantity =
                firstDefinedNumber(
                    rawItem?.unitQuantity,
                    rawItem?.unit_quantity,
                    rawItem?.quantity,
                    rawItem?.qty,
                ) || 1;
            const explicitUnitPrice = firstDefinedNumber(
                rawItem?.unitPrice,
                rawItem?.unit_price,
                rawItem?.salesPrice,
                rawItem?.price,
            );
            const explicitSellingRate = firstDefinedNumber(
                rawItem?.sellingRate,
                rawItem?.selling_rate,
                rawItem?.salesAmount,
            );
            const amountBasedUnitPrice =
                firstDefinedNumber(rawItem?.amount) / unitQuantity;
            const unitPrice =
                explicitUnitPrice > 0
                    ? explicitUnitPrice
                    : explicitSellingRate > 0
                        ? explicitSellingRate
                        : amountBasedUnitPrice;

            return {
                ...rawItem,
                commodity:
                    rawItem?.commodity ||
                    rawItem?.invoiceItem ||
                    rawItem?.invoice_item ||
                    rawItem?.label ||
                    "",
                description: rawItem?.description || "",
                totalCost: firstDefinedNumber(
                    rawItem?.totalCost,
                    rawItem?.total_cost,
                    rawItem?.cost,
                    rawItem?.unitCost,
                    rawItem?.unit_cost,
                ),
                costCurrency: normalizeCurrencyCode(
                    rawItem?.costCurrency || rawItem?.cost_currency || costCurrency,
                ),
                unitPrice,
                sellingRate: unitPrice,
                sellingRateCurrency: normalizeCurrencyCode(
                    rawItem?.sellingRateCurrency ||
                    rawItem?.selling_rate_currency ||
                    rawItem?.currency ||
                    sellingCurrency,
                ),
                unitQuantity,
                amount: unitPrice * unitQuantity,
            };
        });
        const invoiceItems = normalizedItems.length
            ? normalizedItems
            : [
                {
                    commodity: "Freight Charge",
                    description: "Shipment freight service",
                    totalCost: getTotalCost(resolvedItem),
                    costCurrency,
                    unitPrice: getExVatInvoiceAmount(resolvedItem),
                    sellingRate: getExVatInvoiceAmount(resolvedItem),
                    sellingRateCurrency: sellingCurrency,
                    unitQuantity: 1,
                    amount: getExVatInvoiceAmount(resolvedItem),
                },
            ];
        const financials = getConvertedFinancials(resolvedItem, sellingCurrency);
        const detectedMode = String(getTransportMode(resolvedItem)).toLowerCase();
        const sourceType = ["road", "ocean", "air"].includes(detectedMode)
            ? detectedMode
            : "";

        navigate("/create-invoice", {
            state: {
                openInvoiceModal: true,
                editMode: true,
                // ViewShipments derives its own isEditMode (PUT vs POST) from
                // this field — without it, this silently created a
                // brand-new duplicate invoice instead of updating the one
                // being edited.
                invoiceId,
                shipments: [
                    {
                        ...resolvedItem,
                        _id: shipmentId,
                        shipmentNo: String(
                            resolvedItem?.shipmentNo || resolvedItem?.jobNo || "",
                        ).replace(/^(OHFL|RHFL|AHFL)/, ""),
                        customerName: getCustomerName(resolvedItem),
                        customer: getCustomerName(resolvedItem),
                        invoiceItems,
                        cargoDetails: invoiceItems,
                        sellingRateCurrency: sellingCurrency,
                        costCurrency,
                        totalCostCurrency: costCurrency,
                        invoiceSalesAmount: getExVatInvoiceAmount(resolvedItem),
                        salesAmount: getExVatInvoiceAmount(resolvedItem),
                        sellingRate: getExVatInvoiceAmount(resolvedItem),
                        totalSellingRate: getExVatInvoiceAmount(resolvedItem),
                        totalAmount: getExVatInvoiceAmount(resolvedItem),
                        customerInvoiceAmount: getExVatInvoiceAmount(resolvedItem),
                        actualRevenueTotal: getExVatInvoiceAmount(resolvedItem),
                        expectedRevenueTotal: getExVatInvoiceAmount(resolvedItem),

                        operationalCost: getOperationalCost(resolvedItem),
                        buyingRate: getBuyingRate(resolvedItem),
                        actualCostTotal: getTotalCost(resolvedItem),
                        expectedCostTotal: getTotalCost(resolvedItem),
                        totalCost: getTotalCost(resolvedItem),
                        totalCostIncludesBuyingRate: true,

                        actualProfit: financials.profit ?? 0,
                        expectedProfit: financials.profit ?? 0,
                        actualMargin: financials.margin ?? 0,

                        sourceType,
                        currency: sellingCurrency,
                    },
                ],
            },
        });
    };

    const getInvoiceDocumentIdCandidates = (item) =>
        [
            item?._documentId,
            item?.invoiceDocumentId,
            item?.invoice_document_id,
            item?.invoiceId,
            item?.invoice_id,
            item?.customerInvoiceId,
            item?.customer_invoice_id,
            item?.invoice?._id,
            item?.invoice?.id,
            item?.invoice?.invoiceId,
            item?.invoice?.invoice_id,
            item?._id,
            item?.id,
        ]
            .map(getEntityId)
            .filter(Boolean)
            .filter((value, index, array) => array.indexOf(value) === index);

    const extractInvoiceRecords = (payload) => {
        const candidates = [
            payload,
            payload?.data,
            payload?.invoices,
            payload?.results,
            payload?.records,
            payload?.data?.data,
            payload?.data?.invoices,
            payload?.data?.results,
            payload?.data?.records,
        ];

        return candidates.find(Array.isArray) || [];
    };

    const extractInvoiceDocument = (payload) =>
        payload?.data?.invoice ||
        payload?.invoice ||
        payload?.data?.data ||
        (payload?.data && !Array.isArray(payload.data) ? payload.data : null) ||
        (payload && !Array.isArray(payload) ? payload : null);

    const resolveInvoiceDocumentId = async (item, fallbackReferenceNo = "") => {
        if (!item) return "";

        const candidates = getInvoiceDocumentIdCandidates(item);

        for (const candidate of candidates) {
            try {
                const response = await fetch(`${API_URL}/invoice/${candidate}`, {
                    headers: {
                        Authorization: `Bearer ${token}`,
                    },
                });

                const payload = await response.json().catch(() => ({}));

                if (!response.ok || payload?.success === false) {
                    continue;
                }

                const invoice = extractInvoiceDocument(payload);

                const resolvedId = getEntityId(
                    invoice?._id || invoice?.id || invoice?.invoiceId,
                );

                if (resolvedId) {
                    return resolvedId;
                }
            } catch (error) {
                console.warn(`Invoice id lookup failed for ${candidate}`, error);
            }
        }

        const shipmentId = getEntityId(
            item?.shipmentId ||
            item?.shipment_id ||
            item?.shipment?._id ||
            item?.shipment?.id,
        );
        const invoiceNo = String(
            item?.invoiceNo ||
            item?.invoice_no ||
            item?.invoiceNumber ||
            item?.invoice_number ||
            fallbackReferenceNo ||
            "",
        ).trim();

        const lookupQueries = [];
        if (shipmentId) {
            lookupQueries.push(`shipmentId=${encodeURIComponent(shipmentId)}`);
        }
        if (invoiceNo) {
            lookupQueries.push(`invoiceNo=${encodeURIComponent(invoiceNo)}`);
        }

        const lookupPaths = [
            lookupQueries.length ? `/invoice?${lookupQueries.join("&")}` : "/invoice",
            invoiceNo
                ? `/finance/customer-invoices?search=${encodeURIComponent(invoiceNo)}&limit=50`
                : "",
            invoiceNo
                ? `/finance/manual-invoices?search=${encodeURIComponent(invoiceNo)}&limit=50`
                : "",
        ].filter(Boolean);

        for (const path of lookupPaths) {
            try {
                const response = await fetch(`${API_URL}${path}`, {
                    headers: { Authorization: `Bearer ${token}` },
                });

                if (!response.ok) continue;

                const payload = await response.json().catch(() => ({}));
                const invoices = extractInvoiceRecords(payload);

                const matchedInvoice = invoices.find((invoice) => {
                    const candidateShipmentId = getEntityId(
                        invoice?.shipmentId ||
                        invoice?.shipment_id ||
                        invoice?.shipment?._id ||
                        invoice?.shipment?.id,
                    );
                    const candidateInvoiceNo = String(
                        invoice?.invoiceNo ||
                        invoice?.invoice_no ||
                        invoice?.invoiceNumber ||
                        invoice?.invoice_number ||
                        "",
                    ).trim();

                    if (shipmentId && candidateShipmentId === shipmentId) return true;
                    return Boolean(invoiceNo && candidateInvoiceNo === invoiceNo);
                });

                const resolvedId = getEntityId(
                    matchedInvoice?._id ||
                    matchedInvoice?.id ||
                    matchedInvoice?.invoiceId ||
                    matchedInvoice?.invoice_id,
                );

                if (resolvedId) return resolvedId;
            } catch (error) {
                console.warn(`${path} invoice document lookup failed`, error);
            }
        }

        return "";
    };

    const openAddCollection = () => {
        setSelectedInvoice(null);
        setCollectionForm({
            amount: "",
            collectionDate: toLocalDateInputValue(),
            paymentMethod: "Bank Transfer",
            bankName: "",
            referenceNo: "",
            note: "",
        });
        setModalOpen(true);
    };

    const openCollectionForInvoice = async (invoice) => {
        const netBalance = getNetBalanceAmount(invoice);

        if (netBalance <= 0.01) {
            appAlert(
                getReturnSummary(invoice).issuedAmount > 0
                    ? financeText("This invoice has no collectible balance after issued returns.")
                    : financeText("This invoice has no outstanding collectible balance."),
            );
            return;
        }

        const documentId = await resolveInvoiceDocumentId(
            invoice,
            getInvoiceNo(invoice),
        );

        setSelectedInvoice({
            ...invoice,
            _documentId: documentId,
        });
        setCollectionForm({
            amount: String(netBalance),
            collectionDate: toLocalDateInputValue(),
            paymentMethod: getPaymentMethod(invoice),
            bankName: getValue(invoice, "bankName", "bank_name", ""),
            referenceNo: getValue(
                invoice,
                "referenceNo",
                "reference_no",
                getInvoiceNo(invoice),
            ),
            note: "",
        });

        setModalOpen(true);
    };

    const saveCollection = async () => {
        if (!selectedInvoice) {
            appAlert(
                financeText(
                    "Please open Add Collection from an invoice row so the payment can be linked to an invoice."
                ),
            );
            return;
        }

        const amount = Number(collectionForm.amount || 0);

        if (!Number.isFinite(amount) || amount <= 0) {
            appAlert(financeText("Collection amount must be greater than zero."));
            return;
        }

        if (!String(collectionForm.referenceNo || "").trim()) {
            appAlert(financeText("Reference number is required."));
            return;
        }

        const invoiceId =
            getEntityId(selectedInvoice?._documentId) ||
            (await resolveInvoiceDocumentId(
                selectedInvoice,
                collectionForm.referenceNo,
            ));

        if (!invoiceId) {
            appAlert(
                `${financeText("Invoice document could not be found for")} ${getInvoiceNo(selectedInvoice)}.`,
            );
            return;
        }

        const previousPaid = getPaidAmount(selectedInvoice);
        const invoiceAmount = getNetInvoiceAmount(selectedInvoice);
        const collectibleBalance = getNetBalanceAmount(selectedInvoice);

        if (amount > collectibleBalance + 0.01) {
            appAlert(
                `${financeText("Collection amount cannot exceed the net collectible balance")} (${formatCurrency(
                    collectibleBalance,
                    getCurrency(selectedInvoice),
                )}).`,
            );
            return;
        }

        const newPaid = previousPaid + amount;
        const newBalance = Math.max(invoiceAmount - newPaid, 0);
        const newStatus =
            invoiceAmount > 0 && newBalance <= 0.01
                ? "Collected"
                : newPaid > 0
                    ? "Partial"
                    : "Pending";

        try {
            const response = await fetch(`${API_URL}/invoice/${invoiceId}`, {
                method: "PATCH",
                headers: {
                    Authorization: `Bearer ${token}`,
                    "Content-Type": "application/json",
                },
                body: JSON.stringify({
                    paidAmount: newPaid,
                    paymentMethod: collectionForm.paymentMethod,
                    bankName: String(collectionForm.bankName || "").trim(),
                    referenceNo: String(collectionForm.referenceNo || "").trim(),
                    collectionDate: collectionForm.collectionDate,
                    collectionNote: String(collectionForm.note || "").trim(),
                }),
            });

            const result = await response.json().catch(() => ({}));

            if (!response.ok) {
                throw new Error(
                    result?.error ||
                    result?.message ||
                    `Invoice collection update failed (${response.status})`,
                );
            }

            const updatedInvoice = extractInvoiceDocument(result);
            const newCollection = {
                ...selectedInvoice,
                ...(updatedInvoice && typeof updatedInvoice === "object"
                    ? updatedInvoice
                    : {}),
                _id: invoiceId,
                invoiceId,
                _documentId: invoiceId,
                amount,
                paidAmount: newPaid,
                balanceAmount: newBalance,
                collectionStatus: newStatus,
                paymentMethod: collectionForm.paymentMethod,
                bankName: String(collectionForm.bankName || "").trim(),
                referenceNo: String(collectionForm.referenceNo || "").trim(),
                collectionDate: collectionForm.collectionDate,
                collectionNote: String(collectionForm.note || "").trim(),
            };

            setCollections((previous) => {
                let replaced = false;
                const next = previous.map((item) => {
                    const itemIds = getInvoiceDocumentIdCandidates(item);
                    const sameInvoiceNumber =
                        normalizeLookupKey(getInvoiceNo(item)) ===
                        normalizeLookupKey(getInvoiceNo(selectedInvoice));

                    if (!itemIds.includes(getEntityId(invoiceId)) && !sameInvoiceNumber) {
                        return item;
                    }

                    replaced = true;
                    return { ...item, ...newCollection };
                });

                return replaced ? next : [newCollection, ...next];
            });

            setModalOpen(false);
            setSelectedInvoice(null);
        } catch (error) {
            console.error("Collection save error:", error);
            appAlert(error?.message || financeText("Collection could not be saved."));
        }
    };

    const openRiskModal = async (invoice) => {
        const documentId = await resolveInvoiceDocumentId(
            invoice,
            getInvoiceNo(invoice),
        );

        setSelectedInvoice({
            ...invoice,
            _documentId: documentId,
        });
        setRiskForm({
            status: getRiskStatus(invoice),
            reason: String(getRiskField(invoice, "reason", "reason", "")),
            expectedRecoveryAmount: String(getExpectedRecoveryAmount(invoice) || ""),
            provisionAmount: String(getProvisionAmount(invoice) || ""),
            writtenOffAmount: String(getWrittenOffAmount(invoice) || ""),
            notes: String(getRiskField(invoice, "notes", "notes", "")),
        });
        setRiskModalOpen(true);
    };

    const saveRiskStatus = async () => {
        if (!selectedInvoice) return;

        const invoiceId =
            getEntityId(selectedInvoice?._documentId) ||
            (await resolveInvoiceDocumentId(
                selectedInvoice,
                getInvoiceNo(selectedInvoice),
            ));

        if (!invoiceId) {
            appAlert(
                `${financeText("Invoice document could not be found for")} ${getInvoiceNo(selectedInvoice)}.`,
            );
            return;
        }

        const balance = getNetBalanceAmount(selectedInvoice);
        const expectedRecoveryAmount = Math.max(
            Number(riskForm.expectedRecoveryAmount || 0),
            0,
        );
        const provisionAmount = Math.max(Number(riskForm.provisionAmount || 0), 0);
        const writtenOffAmount = Math.max(
            Number(riskForm.writtenOffAmount || 0),
            0,
        );

        if (
            ["doubtful", "bad_debt", "written_off"].includes(riskForm.status) &&
            !riskForm.reason.trim()
        ) {
            appAlert(financeText("A reason is required."));
            return;
        }

        if (
            [expectedRecoveryAmount, provisionAmount, writtenOffAmount].some(
                (value) => value > balance + 0.01,
            )
        ) {
            appAlert(financeText("Risk amounts cannot exceed the net collectible balance."));
            return;
        }

        const collectionRisk = {
            status: normalizeRiskStatus(riskForm.status),
            flaggedAt: new Date().toISOString(),
            reason: riskForm.reason.trim(),
            expectedRecoveryAmount,
            provisionAmount,
            writtenOffAmount:
                riskForm.status === "written_off" ? writtenOffAmount : 0,
            writtenOffAt:
                riskForm.status === "written_off" ? new Date().toISOString() : null,
            notes: riskForm.notes.trim(),
        };

        try {
            setRiskSaving(true);

            const response = await fetch(`${API_URL}/invoice/${invoiceId}`, {
                method: "PATCH",
                headers: {
                    Authorization: `Bearer ${token}`,
                    "Content-Type": "application/json",
                },
                body: JSON.stringify({
                    collectionRisk,
                    riskStatus: collectionRisk.status,
                }),
            });

            const result = await response.json().catch(() => ({}));

            if (!response.ok) {
                throw new Error(
                    result?.error ||
                    result?.message ||
                    `Risk status update failed (${response.status})`,
                );
            }

            const updatedInvoice = extractInvoiceDocument(result);

            setCollections((previous) =>
                previous.map((item) => {
                    const itemIds = getInvoiceDocumentIdCandidates(item);
                    const sameInvoiceNumber =
                        normalizeLookupKey(getInvoiceNo(item)) ===
                        normalizeLookupKey(getInvoiceNo(selectedInvoice));

                    if (!itemIds.includes(getEntityId(invoiceId)) && !sameInvoiceNumber) {
                        return item;
                    }

                    return {
                        ...item,
                        ...(updatedInvoice && typeof updatedInvoice === "object"
                            ? updatedInvoice
                            : {}),
                        invoiceId,
                        _documentId: invoiceId,
                        collectionRisk,
                        riskStatus: collectionRisk.status,
                    };
                }),
            );

            setRiskModalOpen(false);
            setSelectedInvoice(null);
        } catch (error) {
            console.error("Collection risk update error:", error);
            appAlert(error?.message || financeText("Collection risk could not be saved."));
        } finally {
            setRiskSaving(false);
        }
    };

    const resetFilters = () => {
        setSearch("");
        setCollectionStatus("all");
        setPaymentMethod("all");
        setStartDate(`${currentYear}-01-01`);
        setEndDate(todayValue);
        setSelectedParty(null);
        setTransportMode("all");
        setInvoiceOrigin("all");
        setCurrencyFilter("all");
        setGeographicScope("all");
        setReturnStatus("all");
        setRiskStatus("all");
        setOverdueRange("all");
    };

    const exportCsv = () => {
        const headers = [
            "Shipment",
            "Invoice No",
            "Customer",
            "Supplier",
            "Geographic Scope",
            "Transport Mode",
            "Invoice Origin",
            "Selling Currency",
            "Cost Currency",
            "Reporting Currency",
            "Collection Date",
            "Amount",
            "Gross Invoice Total",
            "Issued Return Amount",
            "Net Invoice Total",
            "Net Collectible Balance",
            "Customer Credit Due",
            "Return Status",
            "Risk Status",
            "Days Overdue",
            "Expected Recovery",
            "Provision Amount",
            "Written Off Amount",
            "Risk Reason",
            "Return Document Count",
            "Latest Return Invoice",
            "Operational Cost",
            "Buying Rate",
            "Total Cost",
            "Profit",
            "Margin",
            "Payment Method",
            "Bank",
            "Reference",
            "Status",
            "Created By",
        ];

        const rows = filteredCollections.map((item) => {
            const financials = getConvertedFinancials(item);

            return [
                getShipmentNo(item),
                getInvoiceNo(item),
                getCustomerName(item),
                getSupplierName(item),
                getGeographicLabel(item, partyCountryLookup),
                MODE_CONFIG[getTransportMode(item)]?.label || getTransportMode(item),
                getInvoiceOrigin(item) === "manual" ? "Manual" : "TMS",
                getCurrency(item),
                getCostCurrency(item),
                reportingCurrency,
                formatDate(getValue(item, "collectionDate", "collection_date")),
                convertCurrency(
                    getAmount(item),
                    getCurrency(item),
                    reportingCurrency,
                ) ?? "Rate unavailable",
                convertCurrency(
                    getInvoiceAmount(item),
                    getCurrency(item),
                    reportingCurrency,
                ) ?? "Rate unavailable",
                convertCurrency(
                    getReturnSummary(item).issuedAmount,
                    getCurrency(item),
                    reportingCurrency,
                ) ?? "Rate unavailable",
                financials.invoice ?? "Rate unavailable",
                convertCurrency(
                    getNetBalanceAmount(item),
                    getCurrency(item),
                    reportingCurrency,
                ) ?? "Rate unavailable",
                convertCurrency(
                    getCustomerCreditAmount(item),
                    getCurrency(item),
                    reportingCurrency,
                ) ?? "Rate unavailable",
                getReturnSummary(item).status,
                RISK_STATUS_STYLES[getRiskStatus(item)]?.label || getRiskStatus(item),
                getDaysOverdue(item),
                convertCurrency(
                    getExpectedRecoveryAmount(item),
                    getCurrency(item),
                    reportingCurrency,
                ) ?? "Rate unavailable",
                convertCurrency(
                    getProvisionAmount(item),
                    getCurrency(item),
                    reportingCurrency,
                ) ?? "Rate unavailable",
                convertCurrency(
                    getWrittenOffAmount(item),
                    getCurrency(item),
                    reportingCurrency,
                ) ?? "Rate unavailable",
                getRiskField(item, "reason", "reason", ""),
                getReturnSummary(item).count,
                getInvoiceNo(getReturnSummary(item).latestReturnInvoice || {}),
                convertCurrency(
                    getOperationalCost(item),
                    getCostCurrency(item),
                    reportingCurrency,
                ) ?? "Rate unavailable",
                convertCurrency(
                    getBuyingRate(item),
                    getCostCurrency(item),
                    reportingCurrency,
                ) ?? "Rate unavailable",
                financials.cost ?? "Rate unavailable",
                financials.profit ?? "Rate unavailable",
                financials.margin === null
                    ? "Rate unavailable"
                    : `${financials.margin.toFixed(2)}%`,
                getPaymentMethod(item),
                getValue(item, "bankName", "bank_name", ""),
                getValue(item, "referenceNo", "reference_no", ""),
                getCollectionStatus(item),
                getValue(item, "createdBy", "created_by", "N/A"),
            ];
        });

        const csv = [headers, ...rows]
            .map((row) =>
                row
                    .map((value) => `"${String(value ?? "").replaceAll('"', '""')}"`)
                    .join(","),
            )
            .join("\n");

        const blob = new Blob([`\uFEFF${csv}`], {
            type: "text/csv;charset=utf-8;",
        });

        const url = URL.createObjectURL(blob);
        const link = document.createElement("a");

        link.href = url;
        const modeName = transportMode === "all" ? "all-modes" : transportMode;
        const scopeName =
            geographicScope === "all" ? "all-scopes" : geographicScope.toLowerCase();
        link.setAttribute(
            "download",
            `collections-${modeName}-${scopeName}-${todayValue}.csv`,
        );
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);

        URL.revokeObjectURL(url);
    };

    const tableData = useMemo(
        () => ({
            columns: [
                {
                    Header: financeText("SHIPMENT"),
                    accessor: "jobNo",
                    width: "14%",
                    Cell: ({ row }) => {
                        const item = row.original;
                        const product = getValue(
                            item,
                            "productType",
                            "product_type",
                            "TMS",
                        );
                        const service = getValue(
                            item,
                            "serviceType",
                            "service_type",
                            "General",
                        );

                        return (
                            <MDBox display="flex" alignItems="center" gap={1.2} py={0.35}>
                                <MDBox
                                    width={38}
                                    height={38}
                                    borderRadius="11px"
                                    display="flex"
                                    alignItems="center"
                                    justifyContent="center"
                                    flexShrink={0}
                                    sx={{
                                        background:
                                            "#EEF4FF",
                                        color: COLORS.blue,
                                        border: "1px solid #D1E0FF",
                                    }}
                                >
                                    <ReceiptLong sx={{ fontSize: "18px !important" }} />
                                </MDBox>

                                <MDBox minWidth={0}>
                                    <MDTypography
                                        variant="button"
                                        fontWeight="bold"
                                        noWrap
                                        sx={{
                                            cursor: "pointer",
                                            color: COLORS.navy,
                                            display: "block",
                                            maxWidth: 145,
                                            "&:hover": {
                                                color: COLORS.blue,
                                            },
                                        }}
                                        onClick={() => openInvoiceDetails(item)}
                                    >
                                        {getShipmentNo(item)}
                                    </MDTypography>

                                    <MDBox
                                        display="flex"
                                        alignItems="center"
                                        gap={0.6}
                                        mt={0.35}
                                        flexWrap="wrap"
                                    >
                                        <Chip
                                            size="small"
                                            label={`${financeText((MODE_CONFIG[getTransportMode(item)] || MODE_CONFIG.manual).label)} · ${getShipmentType(item)}`}
                                            sx={{
                                                height: 21,
                                                color: (
                                                    MODE_CONFIG[getTransportMode(item)] ||
                                                    MODE_CONFIG.manual
                                                ).color,
                                                background: (
                                                    MODE_CONFIG[getTransportMode(item)] ||
                                                    MODE_CONFIG.manual
                                                ).light,
                                                border: `1px solid ${(MODE_CONFIG[getTransportMode(item)] || MODE_CONFIG.manual).border}`,
                                                fontSize: "9px",
                                                fontWeight: 800,
                                            }}
                                        />
                                        <MDTypography
                                            variant="caption"
                                            noWrap
                                            sx={{ color: COLORS.subtext }}
                                        >
                                            {product} · {service}
                                        </MDTypography>
                                    </MDBox>
                                </MDBox>
                            </MDBox>
                        );
                    },
                },
                {
                    Header: financeText("INVOICE"),
                    accessor: "invoiceNo",
                    width: "12%",
                    Cell: ({ row }) => (
                        <MDBox>
                            <MDTypography
                                variant="button"
                                fontWeight="bold"
                                sx={{ color: COLORS.text }}
                            >
                                {getInvoiceNo(row.original)}
                            </MDTypography>

                            <MDTypography
                                variant="caption"
                                display="block"
                                sx={{ color: COLORS.subtext }}
                            >
                                {financeText("Payment reference")}
                            </MDTypography>
                        </MDBox>
                    ),
                },
                {
                    Header: financeText("CUSTOMER"),
                    accessor: "customerName",
                    width: "16%",
                    Cell: ({ row }) => {
                        const name = getCustomerName(row.original);
                        const taxNo = getValue(
                            row.original,
                            "customerTaxNo",
                            "customer_tax_no",
                            "-",
                        );

                        return (
                            <MDBox display="flex" alignItems="center" gap={1} minWidth={0}>
                                <Avatar
                                    sx={{
                                        width: 34,
                                        height: 34,
                                        background: "#0B1F3A",
                                        color: "#FFFFFF",
                                        fontSize: "11px",
                                        fontWeight: 800,
                                        boxShadow: "none",
                                    }}
                                >
                                    {name.slice(0, 2).toUpperCase()}
                                </Avatar>

                                <MDBox minWidth={0}>
                                    <MDTypography
                                        variant="button"
                                        fontWeight="bold"
                                        noWrap
                                        display="block"
                                        sx={{
                                            color: COLORS.text,
                                            maxWidth: 145,
                                        }}
                                    >
                                        {name}
                                    </MDTypography>

                                    <MDTypography
                                        variant="caption"
                                        noWrap
                                        display="block"
                                        sx={{
                                            color: COLORS.subtext,
                                            maxWidth: 145,
                                        }}
                                    >
                                        {financeText("Tax No")}: {taxNo}
                                    </MDTypography>
                                </MDBox>
                            </MDBox>
                        );
                    },
                },
                {
                    Header: financeText("COLLECTION DATE"),
                    accessor: "collectionDate",
                    width: "11%",
                    Cell: ({ row }) => (
                        <MDBox>
                            <MDTypography
                                variant="button"
                                fontWeight="bold"
                                sx={{ color: COLORS.text }}
                            >
                                {formatDate(
                                    getValue(row.original, "collectionDate", "collection_date"),
                                )}
                            </MDTypography>

                            <MDTypography
                                variant="caption"
                                display="block"
                                sx={{ color: COLORS.subtext }}
                            >
                                {financeText("Value date")}
                            </MDTypography>
                        </MDBox>
                    ),
                },
                {
                    Header: financeText("AMOUNT"),
                    accessor: "amount",
                    align: "right",
                    width: "11%",
                    Cell: ({ row }) => (
                        <MDBox textAlign="right">
                            <MDTypography
                                variant="button"
                                fontWeight="bold"
                                sx={{ color: COLORS.green }}
                            >
                                {formatCompact(
                                    getAmount(row.original),
                                    getCurrency(row.original),
                                )}
                            </MDTypography>

                            <MDTypography
                                variant="caption"
                                display="block"
                                sx={{ color: COLORS.subtext }}
                            >
                                {financeText("Incoming payment")}
                            </MDTypography>
                        </MDBox>
                    ),
                },
                {
                    Header: financeText("NET RECEIVABLE"),
                    accessor: "netReceivable",
                    align: "right",
                    width: "13%",
                    Cell: ({ row }) => {
                        const item = row.original;
                        const summary = getReturnSummary(item);
                        const currency = getCurrency(item);

                        return (
                            <MDBox textAlign="right" minWidth={130}>
                                <MDTypography
                                    variant="button"
                                    fontWeight="bold"
                                    sx={{ color: COLORS.text }}
                                >
                                    {formatCompact(getNetInvoiceAmount(item), currency)}
                                </MDTypography>
                                {hasVatIncluded(item) && (
                                    <MDTypography
                                        variant="caption"
                                        display="block"
                                        sx={{ color: COLORS.subtext }}
                                    >
                                        {financeText("Incl. VAT")}{" "}
                                        {formatCompact(getVatAmount(item), currency)}
                                    </MDTypography>
                                )}
                                <MDTypography
                                    variant="caption"
                                    display="block"
                                    sx={{ color: COLORS.subtext }}
                                >
                                    {financeText("Balance")} {formatCompact(getNetBalanceAmount(item), currency)}
                                </MDTypography>
                                {summary.issuedAmount > 0 && (
                                    <MDTypography
                                        variant="caption"
                                        display="block"
                                        sx={{ color: COLORS.purple }}
                                    >
                                        {financeText("Returned")} {formatCompact(summary.issuedAmount, currency)}
                                    </MDTypography>
                                )}
                                {getCustomerCreditAmount(item) > 0.01 && (
                                    <MDTypography
                                        variant="caption"
                                        display="block"
                                        sx={{ color: COLORS.red, fontWeight: 700 }}
                                    >
                                        {financeText("Credit due")}{" "}
                                        {formatCompact(getCustomerCreditAmount(item), currency)}
                                    </MDTypography>
                                )}
                            </MDBox>
                        );
                    },
                },
                {
                    Header: financeText("CREDIT RISK"),
                    accessor: "riskStatus",
                    width: "14%",
                    Cell: ({ row }) => {
                        const item = row.original;
                        const status = getRiskStatus(item);
                        const style = RISK_STATUS_STYLES[status];
                        const days = getDaysOverdue(item);
                        return (
                            <MDBox minWidth={125}>
                                <Chip
                                    size="small"
                                    label={financeText(style.label)}
                                    sx={{
                                        color: style.color,
                                        background: style.background,
                                        border: `1px solid ${style.border}`,
                                        fontWeight: 800,
                                    }}
                                />
                                <MDTypography
                                    variant="caption"
                                    display="block"
                                    mt={0.45}
                                    sx={{
                                        color: days > 0 ? COLORS.red : COLORS.subtext,
                                        fontWeight: days > 0 ? 700 : 400,
                                    }}
                                >
                                    {days > 0 ? `${days} ${financeText("days overdue")}` : financeText("Current")}
                                </MDTypography>
                                {getProvisionAmount(item) > 0 && (
                                    <MDTypography
                                        variant="caption"
                                        display="block"
                                        sx={{ color: COLORS.purple }}
                                    >
                                        {financeText("Provision")}{" "}
                                        {formatCompact(getProvisionAmount(item), getCurrency(item))}
                                    </MDTypography>
                                )}
                            </MDBox>
                        );
                    },
                },
                {
                    Header: financeText("PROFITABILITY"),
                    accessor: "actualProfit",
                    align: "right",
                    width: "12%",
                    Cell: ({ row }) => {
                        const item = row.original;
                        const financials = getConvertedFinancials(item);
                        const profit = financials.profit;
                        const margin = financials.margin;
                        const ratesAvailable = profit !== null;
                        const positive = profit >= 0;

                        return (
                            <MDBox textAlign="right">
                                <MDTypography
                                    variant="button"
                                    fontWeight="bold"
                                    sx={{
                                        color: !ratesAvailable
                                            ? COLORS.amber
                                            : positive
                                                ? COLORS.green
                                                : COLORS.red,
                                    }}
                                >
                                    {ratesAvailable
                                        ? formatCompact(profit, reportingCurrency)
                                        : financeText("Rate unavailable")}
                                </MDTypography>

                                <MDTypography
                                    variant="caption"
                                    display="block"
                                    sx={{
                                        color: positive ? COLORS.green : COLORS.red,
                                    }}
                                >
                                    {ratesAvailable
                                        ? `${margin.toFixed(1)}% ${financeText("margin")}`
                                        : financeText("Missing exchange rate")}
                                </MDTypography>
                            </MDBox>
                        );
                    },
                },
                {
                    Header: financeText("METHOD"),
                    accessor: "paymentMethod",
                    width: "10%",
                    Cell: ({ row }) => (
                        <Chip
                            size="small"
                            label={financeText(getPaymentMethod(row.original))}
                            sx={{
                                color: COLORS.navy,
                                background: "#EEF2F6",
                                fontWeight: 700,
                                borderRadius: "8px",
                            }}
                        />
                    ),
                },
                {
                    Header: financeText("BANK"),
                    accessor: "bankName",
                    width: "10%",
                    Cell: ({ row }) => (
                        <MDBox display="flex" alignItems="center" gap={0.7}>
                            <AccountBalance
                                sx={{
                                    fontSize: "16px !important",
                                    color: COLORS.blue,
                                }}
                            />
                            <MDTypography
                                variant="button"
                                fontWeight="medium"
                                sx={{ color: COLORS.text }}
                            >
                                {getValue(row.original, "bankName", "bank_name", "-")}
                            </MDTypography>
                        </MDBox>
                    ),
                },
                {
                    Header: financeText("REFERENCE"),
                    accessor: "referenceNo",
                    width: "11%",
                    Cell: ({ row }) => (
                        <MDBox>
                            <MDTypography
                                variant="button"
                                fontWeight="bold"
                                sx={{ color: COLORS.text }}
                            >
                                {getValue(row.original, "referenceNo", "reference_no", "-")}
                            </MDTypography>

                            <MDTypography
                                variant="caption"
                                display="block"
                                sx={{ color: COLORS.subtext }}
                            >
                                {financeText("Bank transaction ref.")}
                            </MDTypography>
                        </MDBox>
                    ),
                },
                {
                    Header: financeText("STATUS"),
                    accessor: "collectionStatus",
                    width: "9%",
                    Cell: ({ row }) => (
                        <FinanceStatusChip status={getCollectionStatus(row.original)} />
                    ),
                },
                {
                    Header: financeText("RETURN STATUS"),
                    accessor: "returnStatus",
                    width: "12%",
                    Cell: ({ row }) => {
                        const summary = getReturnSummary(row.original);
                        const style =
                            RETURN_STATUS_STYLES[summary.status] ||
                            RETURN_STATUS_STYLES["No Return"];

                        return (
                            <MDBox minWidth={125}>
                                <Chip
                                    size="small"
                                    label={financeText(summary.status)}
                                    sx={{
                                        color: style.color,
                                        background: style.background,
                                        border: `1px solid ${style.border}`,
                                        fontWeight: 800,
                                    }}
                                />
                                {summary.count > 0 && (
                                    <MDTypography
                                        variant="caption"
                                        display="block"
                                        mt={0.45}
                                        sx={{ color: COLORS.subtext }}
                                    >
                                        {summary.count} {financeText(summary.count === 1 ? "document" : "documents")}
                                    </MDTypography>
                                )}
                            </MDBox>
                        );
                    },
                },
                {
                    Header: financeText("ACTIONS"),
                    accessor: "actions",
                    align: "right",
                    width: "9%",
                    Cell: ({ row }) => {
                        const item = row.original;
                        const returnSummary = getReturnSummary(item);
                        const canCollect = getNetBalanceAmount(item) > 0.01;

                        return (
                            <MDBox display="flex" justifyContent="flex-end" gap={0.55}>
                                <Tooltip title={financeText("View related invoice")} arrow>
                                    <IconButton
                                        size="small"
                                        onClick={() => openInvoiceDetails(item)}
                                        sx={{
                                            color: COLORS.blue,
                                            background: COLORS.blueLight,
                                            border: "1px solid #B2DDFF",
                                            "&:hover": {
                                                background: "#D1E9FF",
                                            },
                                        }}
                                    >
                                        <Visibility sx={{ fontSize: "17px !important" }} />
                                    </IconButton>
                                </Tooltip>

                                {returnSummary.latestReturnInvoice && (
                                    <Tooltip title={financeText("Open latest return invoice")} arrow>
                                        <IconButton
                                            size="small"
                                            onClick={() => openLatestReturnInvoice(item)}
                                            sx={{
                                                color: COLORS.purple,
                                                background: COLORS.purpleLight,
                                                border: "1px solid #D9D6FE",
                                                "&:hover": { background: "#E9E7FF" },
                                            }}
                                        >
                                            <ReceiptLong sx={{ fontSize: "17px !important" }} />
                                        </IconButton>
                                    </Tooltip>
                                )}

                                <Tooltip title={financeText("Manage collection risk")} arrow>
                                    <IconButton
                                        size="small"
                                        onClick={() => openRiskModal(item)}
                                        sx={{
                                            color: COLORS.red,
                                            background: COLORS.redLight,
                                            border: "1px solid #FECDCA",
                                            "&:hover": { background: "#FEE4E2" },
                                        }}
                                    >
                                        <WarningAmber sx={{ fontSize: "17px !important" }} />
                                    </IconButton>
                                </Tooltip>

                                <Tooltip
                                    title={
                                        canCollect
                                            ? financeText("Add collection")
                                            : financeText("No collectible balance remains")
                                    }
                                    arrow
                                >
                  <span>
                    <IconButton
                        size="small"
                        disabled={!canCollect}
                        onClick={() => openCollectionForInvoice(item)}
                        sx={{
                            color: COLORS.green,
                            background: COLORS.greenLight,
                            border: "1px solid #ABEFC6",
                            "&:hover": {
                                background: "#D1FADF",
                            },
                            "&.Mui-disabled": {
                                color: "#98A2B3",
                                background: "#F2F4F7",
                                borderColor: "#D0D5DD",
                            },
                        }}
                    >
                      <Paid sx={{ fontSize: "17px !important" }} />
                    </IconButton>
                  </span>
                                </Tooltip>
                            </MDBox>
                        );
                    },
                },
            ],
            rows: filteredCollections,
        }),
        [filteredCollections, getConvertedFinancials, reportingCurrency, navigate, financeLanguage],
    );

    return (
        <DashboardLayout>
            <DashboardNavbar />

            <MDBox
                pt={6}
                pb={3}
                sx={{
                    minHeight: "100vh",
                    background: COLORS.page,
                    mx: { xs: -2, md: -3 },
                    px: { xs: 2, md: 3 },
                }}
            >
                <Card
                    sx={{
                        borderRadius: "22px",
                        color: COLORS.white,
                        background:
                            "linear-gradient(125deg, #081A31 0%, #0D2D52 52%, #175CD3 135%)",
                        boxShadow: "0 18px 46px rgba(11, 31, 58, 0.22)",
                        overflow: "hidden",
                        position: "relative",
                        mb: 3,
                        "&::after": {
                            content: '""',
                            position: "absolute",
                            width: 360,
                            height: 360,
                            borderRadius: "50%",
                            right: -130,
                            top: -180,
                            background: "rgba(255,255,255,.07)",
                        },
                        "&::before": {
                            content: '""',
                            position: "absolute",
                            width: 230,
                            height: 230,
                            borderRadius: "50%",
                            right: 120,
                            bottom: -170,
                            background: "rgba(11,165,236,.16)",
                        },
                    }}
                >
                    <MDBox p={{ xs: 2.5, md: 3.5 }} position="relative" zIndex={1}>
                        <Grid container spacing={3} alignItems="center">
                            <Grid item xs={12} lg={7}>
                                <MDBox display="flex" alignItems="center" gap={1} mb={1}>
                                    <MDBox
                                        width={34}
                                        height={34}
                                        borderRadius="10px"
                                        display="flex"
                                        alignItems="center"
                                        justifyContent="center"
                                        sx={{
                                            background: "rgba(255,255,255,.12)",
                                        }}
                                    >
                                        <WorkspacePremium
                                            sx={{
                                                color: "#FFFFFF",
                                                fontSize: 20,
                                            }}
                                        />
                                    </MDBox>

                                    <MDTypography
                                        variant="caption"
                                        fontWeight="bold"
                                        sx={{
                                            color: "rgba(255,255,255,.72)",
                                            letterSpacing: ".12em",
                                        }}
                                    >
                                        {financeText("CORPORATE COLLECTIONS CENTER")}
                                    </MDTypography>
                                </MDBox>

                                <MDTypography
                                    variant="h2"
                                    fontWeight="bold"
                                    sx={{
                                        color: "#FFFFFF",
                                        lineHeight: 1.12,
                                    }}
                                >
                                    {financeText("Collections")}
                                </MDTypography>

                                <MDTypography
                                    variant="body2"
                                    mt={1}
                                    sx={{
                                        color: "rgba(255,255,255,.72)",
                                        maxWidth: 720,
                                    }}
                                >
                                    {financeText(
                                        "Manage incoming customer payments, bank references, payment methods, matching status and invoice balances from one banking-grade workspace."
                                    )}
                                </MDTypography>

                                <MDBox
                                    display="flex"
                                    alignItems="center"
                                    flexWrap="wrap"
                                    gap={1}
                                    mt={2.5}
                                >
                                    <Chip
                                        icon={
                                            <CheckCircle
                                                sx={{
                                                    color: "#75E0A7 !important",
                                                }}
                                            />
                                        }
                                        label={`${filteredCollections.length} ${financeText("collection records")}`}
                                        sx={{
                                            color: "#FFFFFF",
                                            background: "rgba(255,255,255,.10)",
                                            border: "1px solid rgba(255,255,255,.14)",
                                        }}
                                    />

                                    <Chip
                                        icon={
                                            <CalendarMonth
                                                sx={{
                                                    color: "#B2DDFF !important",
                                                }}
                                            />
                                        }
                                        label={`${financeText("Updated")} ${lastUpdated.toLocaleTimeString([], {
                                            hour: "2-digit",
                                            minute: "2-digit",
                                        })}`}
                                        sx={{
                                            color: "#FFFFFF",
                                            background: "rgba(255,255,255,.10)",
                                            border: "1px solid rgba(255,255,255,.14)",
                                        }}
                                    />
                                </MDBox>
                            </Grid>

                            <Grid item xs={12} lg={5}>
                                <MDBox
                                    p={2}
                                    borderRadius="16px"
                                    sx={{
                                        background: "rgba(255,255,255,.09)",
                                        border: "1px solid rgba(255,255,255,.13)",
                                        backdropFilter: "blur(8px)",
                                    }}
                                >
                                    <MDBox
                                        display="flex"
                                        justifyContent="space-between"
                                        alignItems="center"
                                        mb={1}
                                    >
                                        <MDTypography
                                            variant="caption"
                                            fontWeight="bold"
                                            sx={{
                                                color: "rgba(255,255,255,.72)",
                                            }}
                                        >
                                            {financeText("TOTAL COLLECTED")}
                                        </MDTypography>

                                        <Paid sx={{ color: "#75E0A7" }} />
                                    </MDBox>

                                    <MDTypography
                                        variant="h3"
                                        fontWeight="bold"
                                        sx={{ color: "#FFFFFF" }}
                                    >
                                        {formatCompact(stats.totalCollected, reportingCurrency)}
                                    </MDTypography>

                                    <MDTypography
                                        variant="caption"
                                        sx={{
                                            color: "rgba(255,255,255,.68)",
                                        }}
                                    >
                                        {financeText("Match rate")} {stats.matchedRate.toFixed(2)}%{" "}
                                        {financeText("across current filter result")}
                                    </MDTypography>

                                    <MDBox display="flex" gap={1} mt={2} alignItems="stretch">
                                        <MDBox
                                            sx={{
                                                display: "flex",
                                                alignItems: "center",
                                                gap: "4px",
                                                p: "4px",
                                                background: "rgba(255,255,255,.10)",
                                                border: "1px solid rgba(255,255,255,.20)",
                                                borderRadius: "12px",
                                                boxShadow: "inset 0 1px 2px rgba(0,0,0,.10)",
                                                flexShrink: 0,
                                            }}
                                        >
                                            <MDBox
                                                component="button"
                                                type="button"
                                                onClick={() =>
                                                    changeFinanceLanguage({
                                                        target: { value: "en" },
                                                    })
                                                }
                                                title="English"
                                                sx={{
                                                    appearance: "none",
                                                    WebkitAppearance: "none",
                                                    border: "none",
                                                    outline: "none",
                                                    cursor: "pointer",
                                                    display: "flex",
                                                    alignItems: "center",
                                                    justifyContent: "center",
                                                    gap: "7px",
                                                    height: 38,
                                                    minWidth: 76,
                                                    px: 1.15,
                                                    borderRadius: "8px",
                                                    background:
                                                        financeLanguage === "en"
                                                            ? "#FFFFFF"
                                                            : "transparent",
                                                    color:
                                                        financeLanguage === "en"
                                                            ? COLORS.navy
                                                            : "#FFFFFF",
                                                    boxShadow:
                                                        financeLanguage === "en"
                                                            ? "0 3px 10px rgba(0,0,0,.18)"
                                                            : "none",
                                                }}
                                            >
                                                <MDBox
                                                    component="img"
                                                    src="/flags/gb.svg"
                                                    alt="English"
                                                    sx={{
                                                        width: 24,
                                                        height: 17,
                                                        objectFit: "cover",
                                                        borderRadius: "3px",
                                                        display: "block",
                                                    }}
                                                />
                                                <MDTypography
                                                    component="span"
                                                    sx={{
                                                        fontSize: "12px",
                                                        fontWeight: 800,
                                                        color: "inherit",
                                                        lineHeight: 1,
                                                    }}
                                                >
                                                    EN
                                                </MDTypography>
                                            </MDBox>

                                            <MDBox
                                                component="button"
                                                type="button"
                                                onClick={() =>
                                                    changeFinanceLanguage({
                                                        target: { value: "tr" },
                                                    })
                                                }
                                                title="Türkçe"
                                                sx={{
                                                    appearance: "none",
                                                    WebkitAppearance: "none",
                                                    border: "none",
                                                    outline: "none",
                                                    cursor: "pointer",
                                                    display: "flex",
                                                    alignItems: "center",
                                                    justifyContent: "center",
                                                    gap: "7px",
                                                    height: 38,
                                                    minWidth: 76,
                                                    px: 1.15,
                                                    borderRadius: "8px",
                                                    background:
                                                        financeLanguage === "tr"
                                                            ? "#FFFFFF"
                                                            : "transparent",
                                                    color:
                                                        financeLanguage === "tr"
                                                            ? COLORS.navy
                                                            : "#FFFFFF",
                                                    boxShadow:
                                                        financeLanguage === "tr"
                                                            ? "0 3px 10px rgba(0,0,0,.18)"
                                                            : "none",
                                                }}
                                            >
                                                <MDBox
                                                    component="img"
                                                    src="/flags/tr.svg"
                                                    alt="Türkçe"
                                                    sx={{
                                                        width: 24,
                                                        height: 17,
                                                        objectFit: "cover",
                                                        borderRadius: "3px",
                                                        display: "block",
                                                    }}
                                                />
                                                <MDTypography
                                                    component="span"
                                                    sx={{
                                                        fontSize: "12px",
                                                        fontWeight: 800,
                                                        color: "inherit",
                                                        lineHeight: 1,
                                                    }}
                                                >
                                                    TR
                                                </MDTypography>
                                            </MDBox>
                                        </MDBox>

                                        <MDButton
                                            variant="contained"
                                            disabled
                                            sx={{
                                                background: "#FFFFFF !important",
                                                color: `${COLORS.navy} !important`,
                                                flex: 1,
                                                boxShadow: "none",
                                                opacity: 0.75,
                                            }}
                                        >
                                            {financeText("Select an invoice below")}
                                        </MDButton>

                                        <Tooltip title={financeText("Refresh collections")}>
                                            <IconButton
                                                onClick={fetchCollections}
                                                sx={{
                                                    color: "#FFFFFF",
                                                    border: "1px solid rgba(255,255,255,.18)",
                                                    borderRadius: "10px",
                                                }}
                                            >
                                                <Refresh />
                                            </IconButton>
                                        </Tooltip>
                                    </MDBox>
                                </MDBox>
                            </Grid>
                        </Grid>
                    </MDBox>
                </Card>

                <Card sx={{ ...cardSx, mb: 2.5 }}>
                    <MDBox p={{ xs: 2, md: 2.5 }}>
                        <Grid container spacing={2} alignItems="center">
                            <Grid item xs={12} sm={6} lg={2}>
                                <TextField
                                    fullWidth
                                    size="small"
                                    type="date"
                                    label={financeText("Start Date")}
                                    value={startDate}
                                    onChange={(event) => setStartDate(event.target.value)}
                                    InputLabelProps={{ shrink: true }}
                                />
                            </Grid>

                            <Grid item xs={12} sm={6} lg={2}>
                                <TextField
                                    fullWidth
                                    size="small"
                                    type="date"
                                    label={financeText("End Date")}
                                    value={endDate}
                                    onChange={(event) => setEndDate(event.target.value)}
                                    InputLabelProps={{ shrink: true }}
                                />
                            </Grid>

                            <Grid item xs={12} sm={6} lg={2}>
                                <TextField
                                    select
                                    fullWidth
                                    size="small"
                                    label={financeText("Customer / Supplier")}
                                    value={
                                        selectedParty
                                            ? `${selectedParty.type}-${selectedParty.name}`
                                            : "all"
                                    }
                                    onChange={(event) => {
                                        if (event.target.value === "all") {
                                            setSelectedParty(null);
                                            return;
                                        }

                                        setSelectedParty(
                                            partyOptions.find(
                                                (option) =>
                                                    `${option.type}-${option.name}` ===
                                                    event.target.value,
                                            ) || null,
                                        );
                                    }}
                                >
                                    <MenuItem value="all">{financeText("All Customers / Suppliers")}</MenuItem>
                                    {partyOptions.map((option) => {
                                        const value = `${option.type}-${option.name}`;
                                        return (
                                            <MenuItem key={value} value={value}>
                                                {financeText(option.label)}
                                            </MenuItem>
                                        );
                                    })}
                                </TextField>
                            </Grid>

                            <Grid item xs={12} sm={6} lg={2}>
                                <TextField
                                    select
                                    fullWidth
                                    size="small"
                                    label={financeText("Invoice Origin")}
                                    value={invoiceOrigin}
                                    onChange={(event) => setInvoiceOrigin(event.target.value)}
                                >
                                    <MenuItem value="all">{financeText("All Origins")}</MenuItem>
                                    <MenuItem value="tms">TMS</MenuItem>
                                    <MenuItem value="manual">{financeText("Manual")}</MenuItem>
                                </TextField>
                            </Grid>

                            <Grid item xs={12} sm={6} lg={2}>
                                <TextField
                                    select
                                    fullWidth
                                    size="small"
                                    label={financeText("Geographic Scope")}
                                    value={geographicScope}
                                    onChange={(event) => setGeographicScope(event.target.value)}
                                >
                                    <MenuItem value="all">{financeText("All Geographic Scopes")}</MenuItem>
                                    <MenuItem value="DOMESTIC">{financeText("Domestic")}</MenuItem>
                                    <MenuItem value="INTERNATIONAL">{financeText("International")}</MenuItem>
                                </TextField>
                            </Grid>

                            <Grid item xs={12} sm={6} lg={2}>
                                <TextField
                                    select
                                    fullWidth
                                    size="small"
                                    label={financeText("Transport Mode")}
                                    value={transportMode}
                                    onChange={(event) => setTransportMode(event.target.value)}
                                >
                                    <MenuItem value="all">{financeText("All Transport Modes")}</MenuItem>
                                    {transportModeOptions.map((mode) => (
                                        <MenuItem key={mode} value={mode}>
                                            {financeText(MODE_CONFIG[mode]?.label || mode)}
                                        </MenuItem>
                                    ))}
                                </TextField>
                            </Grid>

                            <Grid item xs={12} sm={6} lg={2}>
                                <TextField
                                    select
                                    fullWidth
                                    size="small"
                                    label={financeText("Currency")}
                                    value={currencyFilter}
                                    onChange={(event) => setCurrencyFilter(event.target.value)}
                                >
                                    <MenuItem value="all">{financeText("All Currencies")}</MenuItem>
                                    {currencyOptions.map((currency) => (
                                        <MenuItem key={currency} value={currency}>
                                            {currency}
                                        </MenuItem>
                                    ))}
                                </TextField>
                            </Grid>

                            {/* Display-only selector: converts the page's
                                aggregated totals into this currency. The
                                "Currency" filter above still controls which
                                records are listed. */}
                            <Grid item xs={12} sm={6} lg={2}>
                                <TextField
                                    select
                                    fullWidth
                                    size="small"
                                    label={financeText("Display Currency")}
                                    value={selectedCurrency}
                                    onChange={(event) => setSelectedCurrency(event.target.value)}
                                >
                                    {CURRENCY_OPTIONS.map((currency) => (
                                        <MenuItem key={currency} value={currency}>
                                            {currency}
                                        </MenuItem>
                                    ))}
                                </TextField>
                            </Grid>

                            <Grid item xs={12} sm={6} lg={2}>
                                <TextField
                                    select
                                    fullWidth
                                    size="small"
                                    label={financeText("Risk Status")}
                                    value={riskStatus}
                                    onChange={(event) => setRiskStatus(event.target.value)}
                                >
                                    <MenuItem value="all">{financeText("All Risk Statuses")}</MenuItem>
                                    {RISK_STATUS_OPTIONS.map((option) => (
                                        <MenuItem key={option.value} value={option.value}>
                                            {financeText(option.label)}
                                        </MenuItem>
                                    ))}
                                </TextField>
                            </Grid>
                            <Grid item xs={12} sm={6} lg={2}>
                                <TextField
                                    select
                                    fullWidth
                                    size="small"
                                    label={financeText("Overdue Range")}
                                    value={overdueRange}
                                    onChange={(event) => setOverdueRange(event.target.value)}
                                >
                                    <MenuItem value="all">{financeText("All Due Dates")}</MenuItem>
                                    <MenuItem value="current">{financeText("Current")}</MenuItem>
                                    <MenuItem value="1-30">{financeText("1-30 Days")}</MenuItem>
                                    <MenuItem value="31-60">{financeText("31-60 Days")}</MenuItem>
                                    <MenuItem value="61-90">{financeText("61-90 Days")}</MenuItem>
                                    <MenuItem value="90+">{financeText("90+ Days")}</MenuItem>
                                </TextField>
                            </Grid>
                            <Grid item xs={12} sm={6} lg={2}>
                                <TextField
                                    select
                                    fullWidth
                                    size="small"
                                    label={financeText("Return Status")}
                                    value={returnStatus}
                                    onChange={(event) => setReturnStatus(event.target.value)}
                                >
                                    {returnStatusOptions.map((status) => (
                                        <MenuItem key={status} value={status}>
                                            {status === "all" ? financeText("All Return Statuses") : financeText(status)}
                                        </MenuItem>
                                    ))}
                                </TextField>
                            </Grid>
                        </Grid>
                    </MDBox>
                </Card>

                {loading ? (
                    <Grid container spacing={SPACE.rowGap}>
                        <Grid item xs={12} lg={7}>
                            <Skeleton variant="rounded" height={260} sx={{ borderRadius: SPACE.radiusCard }} />
                        </Grid>
                        <Grid item xs={12} lg={5}>
                            <Skeleton variant="rounded" height={260} sx={{ borderRadius: SPACE.radiusCard }} />
                        </Grid>
                    </Grid>
                ) : (
                    <>
                        {/* Same hero + secondary-tiles pattern as the Finance
                            Dashboard, from the shared FinanceUiKit. */}

                        <Grid container spacing={SPACE.rowGap} mt={0.5}>
                            <Grid item xs={12} sm={6} lg={2.4}>
                                <FinanceKpiTile
                                    title={financeText("Collections")}
                                    value={formatNumber(stats.count)}
                                    subtitle={financeText("Filtered payment records")}
                                    icon={<ReceiptLong />}
                                    tone="navy"
                                />
                            </Grid>
                            <Grid item xs={12} sm={6} lg={2.4}>
                                <FinanceKpiTile
                                    title={financeText("Pending")}
                                    value={formatCompact(stats.pendingAmount, reportingCurrency)}
                                    subtitle={financeText("Net collectible balance after returns")}
                                    icon={<WarningAmber />}
                                    tone="amber"
                                />
                            </Grid>
                            <Grid item xs={12} sm={6} lg={2.4}>
                                <FinanceKpiTile
                                    title={financeText("Returned")}
                                    value={formatCompact(stats.returnedAmount, reportingCurrency)}
                                    subtitle={financeText("Issued returns reducing receivables")}
                                    icon={<ReceiptLong />}
                                    tone="purple"
                                />
                            </Grid>
                            <Grid item xs={12} sm={6} lg={2.4}>
                                <FinanceKpiTile
                                    title={financeText("Matched")}
                                    value={formatNumber(stats.matchedCount)}
                                    subtitle={`${financeText("Match rate")} ${stats.matchedRate.toFixed(1)}%`}
                                    icon={<CheckCircle />}
                                    tone="blue"
                                />
                            </Grid>
                            <Grid item xs={12} sm={6} lg={2.4}>
                                <FinanceKpiTile
                                    title={financeText("Written Off")}
                                    value={formatCompact(stats.writtenOffAmount, reportingCurrency)}
                                    subtitle={financeText("Accounting write-off total")}
                                    icon={<ReceiptLong />}
                                    tone="navy"
                                />
                            </Grid>
                        </Grid>



                        <Card sx={{ ...cardSx, mt: 2.5 }}>
                            <MDBox p={3}>
                                <MDBox
                                    display="flex"
                                    alignItems={{
                                        xs: "flex-start",
                                        md: "center",
                                    }}
                                    justifyContent="space-between"
                                    flexDirection={{
                                        xs: "column",
                                        md: "row",
                                    }}
                                    gap={1.5}
                                    mb={2.5}
                                >
                                    <MDBox>
                                        <MDBox display="flex" alignItems="center" gap={1}>
                                            <MDBox
                                                width={38}
                                                height={38}
                                                borderRadius="11px"
                                                display="flex"
                                                alignItems="center"
                                                justifyContent="center"
                                                sx={{
                                                    color: COLORS.blue,
                                                    background: COLORS.blueLight,
                                                    border: "1px solid #B2DDFF",
                                                }}
                                            >
                                                <FilterAlt sx={{ fontSize: 19 }} />
                                            </MDBox>

                                            <MDBox>
                                                <MDTypography
                                                    variant="h6"
                                                    fontWeight="bold"
                                                    sx={{ color: COLORS.text }}
                                                >
                                                    {financeText("Collection Filters")}
                                                </MDTypography>

                                                <MDTypography
                                                    variant="caption"
                                                    sx={{ color: COLORS.subtext }}
                                                >
                                                    {financeText(
                                                        "Search and segment payments by shipment, customer, reference, status and method."
                                                    )}
                                                </MDTypography>
                                            </MDBox>
                                        </MDBox>
                                    </MDBox>

                                    <MDBox display="flex" flexWrap="wrap" gap={1}>
                                        <MDButton
                                            variant="outlined"
                                            color="dark"
                                            size="small"
                                            onClick={fetchCollections}
                                            sx={{ borderRadius: "10px" }}
                                        >
                                            <Refresh sx={{ mr: 0.5 }} />
                                            {financeText("Refresh")}
                                        </MDButton>

                                        <MDButton
                                            variant="outlined"
                                            color="dark"
                                            size="small"
                                            onClick={resetFilters}
                                            sx={{ borderRadius: "10px" }}
                                        >
                                            <RestartAlt sx={{ mr: 0.5 }} />
                                            {financeText("Reset")}
                                        </MDButton>

                                        <MDButton
                                            variant="contained"
                                            size="small"
                                            onClick={exportCsv}
                                            sx={{
                                                borderRadius: "10px",
                                                background: `${COLORS.navy} !important`,
                                                color: "#FFFFFF !important",
                                                boxShadow: "none",
                                            }}
                                        >
                                            <Download sx={{ mr: 0.5 }} />
                                            {financeText("Export CSV")}
                                        </MDButton>

                                        <MDButton
                                            variant="outlined"
                                            color="dark"
                                            size="small"
                                            onClick={() =>
                                                navigate("/invoice/finance/customer-invoices")
                                            }
                                            sx={{ borderRadius: "10px" }}
                                        >
                                            {financeText("Customer Invoices")}
                                        </MDButton>
                                    </MDBox>
                                </MDBox>

                                <Grid container spacing={2}>
                                    <Grid item xs={12} lg={4}>
                                        <TextField
                                            fullWidth
                                            size="small"
                                            label={financeText("Search")}
                                            placeholder={financeText("Shipment, invoice, customer, reference...")}
                                            value={search}
                                            onChange={(event) => setSearch(event.target.value)}
                                            InputProps={{
                                                startAdornment: (
                                                    <InputAdornment position="start">
                                                        <Search
                                                            fontSize="small"
                                                            sx={{ color: COLORS.subtext }}
                                                        />
                                                    </InputAdornment>
                                                ),
                                            }}
                                            sx={{
                                                "& .MuiOutlinedInput-root": {
                                                    borderRadius: "11px",
                                                    background: "#FFFFFF",
                                                },
                                            }}
                                        />
                                    </Grid>

                                    <Grid item xs={12} sm={6} lg={4}>
                                        <TextField
                                            select
                                            fullWidth
                                            size="small"
                                            label={financeText("Collection Status")}
                                            value={collectionStatus}
                                            onChange={(event) =>
                                                setCollectionStatus(event.target.value)
                                            }
                                            sx={{
                                                "& .MuiOutlinedInput-root": {
                                                    borderRadius: "11px",
                                                    background: "#FFFFFF",
                                                },
                                            }}
                                        >
                                            {statusOptions.statuses.map((item) => (
                                                <MenuItem key={item} value={item}>
                                                    {item === "all" ? financeText("All Statuses") : financeText(item)}
                                                </MenuItem>
                                            ))}
                                        </TextField>
                                    </Grid>

                                    <Grid item xs={12} sm={6} lg={4}>
                                        <TextField
                                            select
                                            fullWidth
                                            size="small"
                                            label={financeText("Payment Method")}
                                            value={paymentMethod}
                                            onChange={(event) => setPaymentMethod(event.target.value)}
                                            sx={{
                                                "& .MuiOutlinedInput-root": {
                                                    borderRadius: "11px",
                                                    background: "#FFFFFF",
                                                },
                                            }}
                                        >
                                            {statusOptions.methods.map((item) => (
                                                <MenuItem key={item} value={item}>
                                                    {item === "all" ? financeText("All Methods") : financeText(item)}
                                                </MenuItem>
                                            ))}
                                        </TextField>
                                    </Grid>
                                </Grid>

                                <MDBox
                                    mt={2.2}
                                    p={1.5}
                                    borderRadius="8px"
                                    display="flex"
                                    alignItems={{
                                        xs: "flex-start",
                                        md: "center",
                                    }}
                                    justifyContent="space-between"
                                    flexDirection={{
                                        xs: "column",
                                        md: "row",
                                    }}
                                    gap={1}
                                    sx={{
                                        background: "#F9FAFB",
                                        border: `1px solid ${COLORS.border}`,
                                    }}
                                >
                                    <MDTypography
                                        variant="caption"
                                        sx={{ color: COLORS.subtext }}
                                    >
                                        {financeText("Showing")}{" "}
                                        <strong style={{ color: COLORS.text }}>
                                            {filteredCollections.length}
                                        </strong>{" "}
                                        {financeText("of")}{" "}
                                        <strong style={{ color: COLORS.text }}>
                                            {collections.length}
                                        </strong>{" "}
                                        {financeText("collection records")}
                                    </MDTypography>

                                    <MDBox display="flex" alignItems="center" gap={1}>
                                        <Chip
                                            size="small"
                                            label={`${financeText("Collected")} ${formatCompact(stats.totalCollected, reportingCurrency)}`}
                                            sx={{
                                                color: COLORS.green,
                                                background: COLORS.greenLight,
                                                fontWeight: 700,
                                            }}
                                        />

                                        <Chip
                                            size="small"
                                            label={`${financeText("Cost")} ${formatCompact(stats.totalCost, reportingCurrency)}`}
                                            sx={{
                                                color: COLORS.red,
                                                background: COLORS.redLight,
                                                fontWeight: 700,
                                            }}
                                        />

                                        <Chip
                                            size="small"
                                            label={`${financeText("Returned")} ${formatCompact(stats.returnedAmount, reportingCurrency)}`}
                                            sx={{
                                                color: COLORS.purple,
                                                background: COLORS.purpleLight,
                                                fontWeight: 700,
                                            }}
                                        />

                                        {stats.customerCreditAmount > 0.01 && (
                                            <Chip
                                                size="small"
                                                label={`${financeText("Credit Due")} ${formatCompact(
                                                    stats.customerCreditAmount,
                                                    reportingCurrency,
                                                )}`}
                                                sx={{
                                                    color: COLORS.red,
                                                    background: COLORS.redLight,
                                                    fontWeight: 700,
                                                }}
                                            />
                                        )}

                                        <Chip
                                            size="small"
                                            label={`${financeText("Profit")} ${formatCompact(stats.profit, reportingCurrency)}`}
                                            sx={{
                                                color: stats.profit >= 0 ? COLORS.green : COLORS.red,
                                                background:
                                                    stats.profit >= 0
                                                        ? COLORS.greenLight
                                                        : COLORS.redLight,
                                                fontWeight: 700,
                                            }}
                                        />

                                        <Chip
                                            size="small"
                                            label={`${financeText("Pending")} ${formatCompact(stats.pendingAmount, reportingCurrency)}`}
                                            sx={{
                                                color: COLORS.amber,
                                                background: COLORS.amberLight,
                                                fontWeight: 700,
                                            }}
                                        />
                                    </MDBox>
                                </MDBox>

                                <Divider sx={{ my: 2.5 }} />

                                <MDBox
                                    sx={{
                                        "& .MuiTableContainer-root": {
                                            border: `1px solid ${COLORS.border}`,
                                            borderRadius: "10px",
                                            overflowX: "auto",
                                            boxShadow: "none",
                                        },
                                        "& table": {
                                            minWidth: 1750,
                                            borderCollapse: "separate",
                                            borderSpacing: 0,
                                        },
                                        "& table thead": {
                                            background:
                                                "#F8FAFC",
                                        },
                                        "& table thead th": {
                                            background: "transparent",
                                            color: "#475467 !important",
                                            fontSize: "10px !important",
                                            fontWeight: "800 !important",
                                            letterSpacing: ".085em",
                                            paddingTop: "15px !important",
                                            paddingBottom: "15px !important",
                                            borderBottom: `1px solid ${COLORS.border} !important`,
                                        },
                                        "& table tbody td": {
                                            paddingTop: "14px !important",
                                            paddingBottom: "14px !important",
                                            borderBottom: "1px solid #F0F2F5 !important",
                                            verticalAlign: "middle",
                                        },
                                        "& table tbody tr": {
                                            background: "#FFFFFF",
                                            transition: "background .18s ease, box-shadow .18s ease",
                                        },
                                        "& table tbody tr:nth-of-type(even)": {
                                            background: "#FCFCFD",
                                        },
                                        "& table tbody tr:hover": {
                                            background: "#F5F8FF !important",
                                            boxShadow: "inset 3px 0 0 #175CD3",
                                        },
                                        "& table tbody tr:last-child td": {
                                            borderBottom: "0 !important",
                                        },
                                        "& .MuiTablePagination-root": {
                                            borderTop: `1px solid ${COLORS.border}`,
                                            background: "#FCFCFD",
                                        },
                                    }}
                                >
                                    <DataTable
                                        table={tableData}
                                        canSearch={false}
                                        entriesPerPage
                                        showTotalEntries
                                        isSorted
                                        noEndBorder
                                    />
                                </MDBox>
                            </MDBox>
                        </Card>

                        {/* İkincil grafik ve kırılımlar: varsayılan kapalı, üstteki filtrelere bağlı */}
                        <FinanceDetailsSection
                            storageKey="collections"
                            title={financeText("Detailed analysis")}
                            subtitle={financeText("Charts and breakdowns for the current filters")}
                        >
                            <Grid container spacing={SPACE.rowGap}>
                            <Grid item xs={12} lg={7}>
                                <FinanceHeroStat
                                    label={financeText("Gross Profit")}
                                    value={formatCompact(stats.profit, reportingCurrency)}
                                    tone={stats.profit >= 0 ? "green" : "red"}
                                    icon={<ArrowUpward />}
                                    meta={`${financeText("Margin")} ${stats.margin.toFixed(2)}%`}
                                    context={[
                                        { label: financeText("Invoice Total"), value: formatCompact(stats.billedTotal, reportingCurrency) },
                                        { label: financeText("Collected"), value: formatCompact(stats.totalCollected, reportingCurrency) },
                                        { label: financeText("Total Cost"), value: formatCompact(stats.totalCost, reportingCurrency) },
                                    ]}
                                />
                            </Grid>

                            <Grid item xs={12} lg={5}>
                                <FinanceCard>
                                    <MDBox p={SPACE.cardPaddingLg} display="flex" flexDirection="column" height="100%">
                                        <FinanceSectionHeader
                                            title={getFinanceLanguage() === "tr" ? "Dikkat gerektiren" : "Needs attention"}
                                        />
                                        <MDBox display="flex" flexDirection="column" gap={1.25}>
                                            {[
                                                { label: financeText("Overdue Receivables"), value: formatCompact(stats.overdueAmount, reportingCurrency), sub: financeText("Outstanding balance past due"), tone: "amber" },
                                                { label: financeText("Doubtful Receivables"), value: formatCompact(stats.doubtfulAmount, reportingCurrency), sub: financeText("Receivables under collection risk"), tone: "purple" },
                                                { label: financeText("Bad Debt Exposure"), value: formatCompact(stats.badDebtAmount, reportingCurrency), sub: `${financeText("Provision")} ${formatCompact(stats.provisionAmount, reportingCurrency)}`, tone: "red" },
                                                { label: financeText("Failed"), value: formatNumber(stats.failedCount), sub: financeText("Failed or cancelled records"), tone: "red" },
                                            ].map((row) => (
                                                <MDBox
                                                    key={row.label}
                                                    display="flex"
                                                    alignItems="center"
                                                    justifyContent="space-between"
                                                    px={1.75}
                                                    py={1.25}
                                                    borderRadius={SPACE.radiusTile}
                                                    sx={{ background: COLORS.muted }}
                                                >
                                                    <MDBox display="flex" alignItems="center" gap={1.5} minWidth={0}>
                                                        <MDBox width={8} height={8} borderRadius="50%" flexShrink={0} sx={{ background: COLORS[row.tone] }} />
                                                        <MDBox minWidth={0}>
                                                            <MDTypography variant="button" fontWeight="medium" sx={{ color: COLORS.text, display: "block" }}>
                                                                {row.label}
                                                            </MDTypography>
                                                            <MDTypography variant="caption" sx={{ color: COLORS.subtext }}>
                                                                {row.sub}
                                                            </MDTypography>
                                                        </MDBox>
                                                    </MDBox>
                                                    <MDTypography variant="button" fontWeight="bold" sx={{ color: COLORS[row.tone], flexShrink: 0 }}>
                                                        {row.value}
                                                    </MDTypography>
                                                </MDBox>
                                            ))}
                                        </MDBox>
                                    </MDBox>
                                </FinanceCard>
                            </Grid>
                        </Grid>

                            <Grid container spacing={2.5} mt={0.1}>
                            {Object.entries(transportSummary).map(([mode, data]) => (
                                <Grid item xs={12} md={4} key={mode}>
                                    <TransportCollectionCard
                                        mode={mode}
                                        data={data}
                                        totalRecords={filteredCollections.length}
                                        currency={reportingCurrency}
                                    />
                                </Grid>
                            ))}
                        </Grid>

                            <Grid container spacing={2.5} mt={0.1}>
                            <Grid item xs={12} lg={7}>
                                <Card sx={{ ...cardSx, height: "100%" }}>
                                    <MDBox p={3}>
                                        <MDBox
                                            display="flex"
                                            justifyContent="space-between"
                                            alignItems="center"
                                            mb={2.5}
                                        >
                                            <MDBox>
                                                <MDTypography
                                                    variant="h6"
                                                    fontWeight="bold"
                                                    sx={{ color: COLORS.text }}
                                                >
                                                    {financeText("Collections by Transport Mode")}
                                                </MDTypography>
                                                <MDTypography
                                                    variant="caption"
                                                    sx={{ color: COLORS.subtext }}
                                                >
                                                    {financeText(
                                                        "Incoming payments and pending amounts across Ocean, Road and Air"
                                                    )}
                                                </MDTypography>
                                            </MDBox>
                                            <MDBox
                                                width={40}
                                                height={40}
                                                borderRadius="8px"
                                                display="flex"
                                                alignItems="center"
                                                justifyContent="center"
                                                sx={{
                                                    color: COLORS.green,
                                                    background: COLORS.greenLight,
                                                    border: "1px solid #ABEFC6",
                                                }}
                                            >
                                                <Paid />
                                            </MDBox>
                                        </MDBox>
                                        <ModeCollectionsChart
                                            summary={transportSummary}
                                            currency={reportingCurrency}
                                        />
                                    </MDBox>
                                </Card>
                            </Grid>

                            <Grid item xs={12} lg={5}>
                                <Card sx={{ ...cardSx, height: "100%" }}>
                                    <MDBox p={3}>
                                        <MDBox
                                            display="flex"
                                            justifyContent="space-between"
                                            alignItems="center"
                                            mb={2.2}
                                        >
                                            <MDBox>
                                                <MDTypography
                                                    variant="h6"
                                                    fontWeight="bold"
                                                    sx={{ color: COLORS.text }}
                                                >
                                                    {financeText("Shipment Type Mix")}
                                                </MDTypography>
                                                <MDTypography
                                                    variant="caption"
                                                    sx={{ color: COLORS.subtext }}
                                                >
                                                    {financeText("Most frequently collected logistics services")}
                                                </MDTypography>
                                            </MDBox>
                                            <MDBox
                                                width={40}
                                                height={40}
                                                borderRadius="8px"
                                                display="flex"
                                                alignItems="center"
                                                justifyContent="center"
                                                sx={{
                                                    color: COLORS.purple,
                                                    background: COLORS.purpleLight,
                                                    border: "1px solid #D9D6FE",
                                                }}
                                            >
                                                <Inventory2 />
                                            </MDBox>
                                        </MDBox>
                                        {shipmentTypeSummary.length ? (
                                            shipmentTypeSummary.map(([type, count], index) => {
                                                const max = shipmentTypeSummary[0]?.[1] || 1;
                                                return (
                                                    <MDBox key={type} mb={1.55}>
                                                        <MDBox
                                                            display="flex"
                                                            justifyContent="space-between"
                                                            mb={0.55}
                                                        >
                                                            <MDTypography
                                                                variant="caption"
                                                                fontWeight="bold"
                                                                sx={{ color: COLORS.text }}
                                                            >
                                                                {index + 1}. {type}
                                                            </MDTypography>
                                                            <MDTypography
                                                                variant="caption"
                                                                fontWeight="bold"
                                                                sx={{ color: COLORS.purple }}
                                                            >
                                                                {count} {financeText("payments")}
                                                            </MDTypography>
                                                        </MDBox>
                                                        <LinearProgress
                                                            variant="determinate"
                                                            value={(count / max) * 100}
                                                            sx={{
                                                                height: 6,
                                                                borderRadius: "999px",
                                                                backgroundColor: "#EEF2F6",
                                                                "& .MuiLinearProgress-bar": {
                                                                    borderRadius: "999px",
                                                                    background:
                                                                        index === 0 ? COLORS.purple : COLORS.blue,
                                                                },
                                                            }}
                                                        />
                                                    </MDBox>
                                                );
                                            })
                                        ) : (
                                            <MDTypography
                                                variant="body2"
                                                sx={{ color: COLORS.subtext }}
                                            >
                                                {financeText("No shipment type data available.")}
                                            </MDTypography>
                                        )}
                                    </MDBox>
                                </Card>
                            </Grid>
                        </Grid>
                        </FinanceDetailsSection>

                    </>
                )}
            </MDBox>

            <Dialog
                open={modalOpen}
                onClose={() => setModalOpen(false)}
                fullWidth
                maxWidth="sm"
                PaperProps={{
                    sx: {
                        borderRadius: "10px",
                        overflow: "hidden",
                        border: `1px solid ${COLORS.border}`,
                        boxShadow: "0 24px 64px rgba(16,24,40,.18)",
                    },
                }}
            >
                <DialogTitle
                    sx={{
                        px: 3,
                        py: 2.5,
                        background:
                            "#0B1F3A",
                        color: "#FFFFFF",
                    }}
                >
                    <MDBox display="flex" alignItems="center" gap={1.2}>
                        <MDBox
                            width={38}
                            height={38}
                            borderRadius="11px"
                            display="flex"
                            alignItems="center"
                            justifyContent="center"
                            sx={{
                                background: "rgba(255,255,255,.12)",
                            }}
                        >
                            <Paid />
                        </MDBox>

                        <MDBox>
                            <MDTypography
                                variant="h6"
                                fontWeight="bold"
                                sx={{ color: "#FFFFFF" }}
                            >
                                {financeText(
                                    selectedInvoice
                                        ? "Add Collection for Invoice"
                                        : "Add Collection"
                                )}
                            </MDTypography>

                            <MDTypography
                                variant="caption"
                                sx={{
                                    color: "rgba(255,255,255,.68)",
                                }}
                            >
                                {financeText("Register an incoming customer payment.")}
                            </MDTypography>
                        </MDBox>
                    </MDBox>
                </DialogTitle>

                <DialogContent sx={{ p: 3 }}>
                    {selectedInvoice && (
                        <Card
                            sx={{
                                ...cardSx,
                                mb: 2.5,
                                mt: 0.5,
                                background: "#F8FAFC",
                            }}
                        >
                            <MDBox p={2}>
                                <MDBox
                                    display="flex"
                                    justifyContent="space-between"
                                    alignItems="center"
                                    gap={1}
                                >
                                    <MDBox>
                                        <MDTypography
                                            variant="button"
                                            fontWeight="bold"
                                            sx={{ color: COLORS.text }}
                                        >
                                            {getInvoiceNo(selectedInvoice)}
                                        </MDTypography>

                                        <MDTypography
                                            variant="caption"
                                            display="block"
                                            sx={{ color: COLORS.subtext }}
                                        >
                                            {getCustomerName(selectedInvoice)}
                                        </MDTypography>
                                    </MDBox>

                                    <MDBox
                                        display="flex"
                                        flexWrap="wrap"
                                        gap={0.7}
                                        justifyContent="flex-end"
                                    >
                                        <Chip
                                            size="small"
                                            label={`${financeText("Cost")} ${formatCurrency(
                                                getTotalCost(selectedInvoice),
                                                getCurrency(selectedInvoice),
                                            )}`}
                                            sx={{
                                                color: COLORS.red,
                                                background: COLORS.redLight,
                                                fontWeight: 700,
                                            }}
                                        />

                                        <Chip
                                            size="small"
                                            label={`${financeText("Profit")} ${formatCurrency(
                                                getExVatNetInvoiceAmount(selectedInvoice) -
                                                getTotalCost(selectedInvoice),
                                                getCurrency(selectedInvoice),
                                            )}`}
                                            sx={{
                                                color:
                                                    getExVatNetInvoiceAmount(selectedInvoice) -
                                                    getTotalCost(selectedInvoice) >=
                                                    0
                                                        ? COLORS.green
                                                        : COLORS.red,
                                                background:
                                                    getExVatNetInvoiceAmount(selectedInvoice) -
                                                    getTotalCost(selectedInvoice) >=
                                                    0
                                                        ? COLORS.greenLight
                                                        : COLORS.redLight,
                                                fontWeight: 700,
                                            }}
                                        />

                                        <Chip
                                            size="small"
                                            label={`${financeText("Balance")} ${formatCurrency(
                                                getNetBalanceAmount(selectedInvoice),
                                                getCurrency(selectedInvoice),
                                            )}`}
                                            sx={{
                                                color: COLORS.red,
                                                background: COLORS.redLight,
                                                fontWeight: 700,
                                            }}
                                        />

                                        {getReturnSummary(selectedInvoice).issuedAmount > 0 && (
                                            <Chip
                                                size="small"
                                                label={`${financeText("Returned")} ${formatCurrency(
                                                    getReturnSummary(selectedInvoice).issuedAmount,
                                                    getCurrency(selectedInvoice),
                                                )}`}
                                                sx={{
                                                    color: COLORS.purple,
                                                    background: COLORS.purpleLight,
                                                    border: "1px solid #D9D6FE",
                                                    fontWeight: 700,
                                                }}
                                            />
                                        )}

                                        {getCustomerCreditAmount(selectedInvoice) > 0.01 && (
                                            <Chip
                                                size="small"
                                                label={`${financeText("Credit Due")} ${formatCurrency(
                                                    getCustomerCreditAmount(selectedInvoice),
                                                    getCurrency(selectedInvoice),
                                                )}`}
                                                sx={{
                                                    color: COLORS.red,
                                                    background: COLORS.redLight,
                                                    border: "1px solid #FECDCA",
                                                    fontWeight: 700,
                                                }}
                                            />
                                        )}
                                    </MDBox>
                                </MDBox>
                            </MDBox>
                        </Card>
                    )}

                    <Grid container spacing={2}>
                        <Grid item xs={12} md={6}>
                            <TextField
                                fullWidth
                                size="small"
                                type="number"
                                label={financeText("Collection Amount")}
                                value={collectionForm.amount}
                                onChange={(event) =>
                                    setCollectionForm((previous) => ({
                                        ...previous,
                                        amount: event.target.value,
                                    }))
                                }
                                inputProps={{
                                    min: 0,
                                    max: selectedInvoice
                                        ? getNetBalanceAmount(selectedInvoice)
                                        : undefined,
                                    step: "0.01",
                                }}
                                sx={{
                                    "& .MuiOutlinedInput-root": {
                                        borderRadius: "11px",
                                    },
                                }}
                            />
                        </Grid>

                        <Grid item xs={12} md={6}>
                            <TextField
                                fullWidth
                                size="small"
                                type="date"
                                label={financeText("Collection Date")}
                                InputLabelProps={{ shrink: true }}
                                value={collectionForm.collectionDate}
                                onChange={(event) =>
                                    setCollectionForm((previous) => ({
                                        ...previous,
                                        collectionDate: event.target.value,
                                    }))
                                }
                                sx={{
                                    "& .MuiOutlinedInput-root": {
                                        borderRadius: "11px",
                                    },
                                }}
                            />
                        </Grid>

                        <Grid item xs={12} md={6}>
                            <TextField
                                select
                                fullWidth
                                size="small"
                                label={financeText("Payment Method")}
                                value={collectionForm.paymentMethod}
                                onChange={(event) =>
                                    setCollectionForm((previous) => ({
                                        ...previous,
                                        paymentMethod: event.target.value,
                                    }))
                                }
                                sx={{
                                    "& .MuiOutlinedInput-root": {
                                        borderRadius: "11px",
                                    },
                                }}
                            >
                                <MenuItem value="Bank Transfer">{financeText("Bank Transfer")}</MenuItem>
                                <MenuItem value="Cash">{financeText("Cash")}</MenuItem>
                                <MenuItem value="Credit Card">{financeText("Credit Card")}</MenuItem>
                                <MenuItem value="Cheque">{financeText("Cheque")}</MenuItem>
                                <MenuItem value="Other">{financeText("Other")}</MenuItem>
                            </TextField>
                        </Grid>

                        <Grid item xs={12} md={6}>
                            <TextField
                                fullWidth
                                size="small"
                                label={financeText("Bank Name")}
                                value={collectionForm.bankName}
                                onChange={(event) =>
                                    setCollectionForm((previous) => ({
                                        ...previous,
                                        bankName: event.target.value,
                                    }))
                                }
                                sx={{
                                    "& .MuiOutlinedInput-root": {
                                        borderRadius: "11px",
                                    },
                                }}
                            />
                        </Grid>

                        <Grid item xs={12}>
                            <TextField
                                fullWidth
                                size="small"
                                label={financeText("Reference No")}
                                value={collectionForm.referenceNo}
                                onChange={(event) =>
                                    setCollectionForm((previous) => ({
                                        ...previous,
                                        referenceNo: event.target.value,
                                    }))
                                }
                                sx={{
                                    "& .MuiOutlinedInput-root": {
                                        borderRadius: "11px",
                                    },
                                }}
                            />
                        </Grid>

                        <Grid item xs={12}>
                            <TextField
                                fullWidth
                                size="small"
                                multiline
                                rows={3}
                                label={financeText("Note")}
                                value={collectionForm.note}
                                onChange={(event) =>
                                    setCollectionForm((previous) => ({
                                        ...previous,
                                        note: event.target.value,
                                    }))
                                }
                                sx={{
                                    "& .MuiOutlinedInput-root": {
                                        borderRadius: "11px",
                                    },
                                }}
                            />
                        </Grid>
                    </Grid>
                </DialogContent>

                <DialogActions
                    sx={{
                        px: 3,
                        py: 2.2,
                        background: "#F9FAFB",
                        borderTop: `1px solid ${COLORS.border}`,
                    }}
                >
                    <MDButton
                        variant="outlined"
                        color="dark"
                        onClick={() => setModalOpen(false)}
                        sx={{ borderRadius: "10px" }}
                    >
                        {financeText("Cancel")}
                    </MDButton>

                    <MDButton
                        variant="contained"
                        onClick={saveCollection}
                        sx={{
                            borderRadius: "10px",
                            background: `${COLORS.navy} !important`,
                            color: "#FFFFFF !important",
                            boxShadow: "none",
                        }}
                    >
                        {financeText("Save Collection")}
                    </MDButton>
                </DialogActions>
            </Dialog>

            <Dialog
                open={riskModalOpen}
                onClose={() => !riskSaving && setRiskModalOpen(false)}
                fullWidth
                maxWidth="sm"
                PaperProps={{ sx: { borderRadius: "10px" } }}
            >
                <DialogTitle
                    sx={{ px: 3, py: 2.4, borderBottom: `1px solid ${COLORS.border}` }}
                >
                    <MDTypography
                        variant="h5"
                        fontWeight="bold"
                        sx={{ color: COLORS.text }}
                    >
                        {financeText("Collection Risk Management")}
                    </MDTypography>
                    <MDTypography variant="caption" sx={{ color: COLORS.subtext }}>
                        {selectedInvoice
                            ? `${getInvoiceNo(selectedInvoice)} · ${getCustomerName(selectedInvoice)}`
                            : financeText("Update receivable risk")}
                    </MDTypography>
                </DialogTitle>
                <DialogContent sx={{ p: 3 }}>
                    {selectedInvoice && (
                        <MDBox
                            mb={2.5}
                            p={1.7}
                            borderRadius="8px"
                            sx={{
                                background: "#F9FAFB",
                                border: `1px solid ${COLORS.border}`,
                            }}
                        >
                            <Grid container spacing={1.5}>
                                <Grid item xs={4}>
                                    <MDTypography
                                        variant="caption"
                                        sx={{ color: COLORS.subtext }}
                                    >
                                        {financeText("Net Balance")}
                                    </MDTypography>
                                    <MDTypography
                                        variant="button"
                                        display="block"
                                        fontWeight="bold"
                                    >
                                        {formatCurrency(
                                            getNetBalanceAmount(selectedInvoice),
                                            getCurrency(selectedInvoice),
                                        )}
                                    </MDTypography>
                                </Grid>
                                <Grid item xs={4}>
                                    <MDTypography
                                        variant="caption"
                                        sx={{ color: COLORS.subtext }}
                                    >
                                        {financeText("Due Date")}
                                    </MDTypography>
                                    <MDTypography
                                        variant="button"
                                        display="block"
                                        fontWeight="bold"
                                    >
                                        {getDueDate(selectedInvoice)
                                            ? formatDate(getDueDate(selectedInvoice))
                                            : "-"}
                                    </MDTypography>
                                </Grid>
                                <Grid item xs={4}>
                                    <MDTypography
                                        variant="caption"
                                        sx={{ color: COLORS.subtext }}
                                    >
                                        {financeText("Days Overdue")}
                                    </MDTypography>
                                    <MDTypography
                                        variant="button"
                                        display="block"
                                        fontWeight="bold"
                                        sx={{
                                            color:
                                                getDaysOverdue(selectedInvoice) > 0
                                                    ? COLORS.red
                                                    : COLORS.green,
                                        }}
                                    >
                                        {getDaysOverdue(selectedInvoice)}
                                    </MDTypography>
                                </Grid>
                            </Grid>
                        </MDBox>
                    )}
                    <Grid container spacing={2}>
                        <Grid item xs={12}>
                            <TextField
                                select
                                fullWidth
                                size="small"
                                label={financeText("Risk Status")}
                                value={riskForm.status}
                                onChange={(event) =>
                                    setRiskForm((p) => ({ ...p, status: event.target.value }))
                                }
                            >
                                {RISK_STATUS_OPTIONS.map((option) => (
                                    <MenuItem key={option.value} value={option.value}>
                                        {financeText(option.label)}
                                    </MenuItem>
                                ))}
                            </TextField>
                        </Grid>
                        <Grid item xs={12}>
                            <TextField
                                fullWidth
                                size="small"
                                label={financeText("Reason")}
                                required={["doubtful", "bad_debt", "written_off"].includes(
                                    riskForm.status,
                                )}
                                value={riskForm.reason}
                                onChange={(event) =>
                                    setRiskForm((p) => ({ ...p, reason: event.target.value }))
                                }
                            />
                        </Grid>
                        <Grid item xs={12} md={4}>
                            <TextField
                                fullWidth
                                size="small"
                                type="number"
                                label={financeText("Expected Recovery")}
                                value={riskForm.expectedRecoveryAmount}
                                onChange={(event) =>
                                    setRiskForm((p) => ({
                                        ...p,
                                        expectedRecoveryAmount: event.target.value,
                                    }))
                                }
                                inputProps={{ min: 0, step: "0.01" }}
                            />
                        </Grid>
                        <Grid item xs={12} md={4}>
                            <TextField
                                fullWidth
                                size="small"
                                type="number"
                                label={financeText("Provision Amount")}
                                value={riskForm.provisionAmount}
                                onChange={(event) =>
                                    setRiskForm((p) => ({
                                        ...p,
                                        provisionAmount: event.target.value,
                                    }))
                                }
                                inputProps={{ min: 0, step: "0.01" }}
                            />
                        </Grid>
                        <Grid item xs={12} md={4}>
                            <TextField
                                fullWidth
                                size="small"
                                type="number"
                                label={financeText("Written-Off Amount")}
                                disabled={riskForm.status !== "written_off"}
                                value={riskForm.writtenOffAmount}
                                onChange={(event) =>
                                    setRiskForm((p) => ({
                                        ...p,
                                        writtenOffAmount: event.target.value,
                                    }))
                                }
                                inputProps={{ min: 0, step: "0.01" }}
                            />
                        </Grid>
                        <Grid item xs={12}>
                            <TextField
                                fullWidth
                                size="small"
                                multiline
                                rows={3}
                                label={financeText("Notes")}
                                value={riskForm.notes}
                                onChange={(event) =>
                                    setRiskForm((p) => ({ ...p, notes: event.target.value }))
                                }
                            />
                        </Grid>
                    </Grid>
                </DialogContent>
                <DialogActions
                    sx={{
                        px: 3,
                        py: 2.2,
                        background: "#F9FAFB",
                        borderTop: `1px solid ${COLORS.border}`,
                    }}
                >
                    <MDButton
                        variant="outlined"
                        color="dark"
                        disabled={riskSaving}
                        onClick={() => setRiskModalOpen(false)}
                    >
                        {financeText("Cancel")}
                    </MDButton>
                    <MDButton
                        variant="contained"
                        disabled={riskSaving}
                        onClick={saveRiskStatus}
                        sx={{
                            background: `${COLORS.red} !important`,
                            color: "#FFFFFF !important",
                        }}
                    >
                        {riskSaving ? financeText("Saving...") : financeText("Save Risk Status")}
                    </MDButton>
                </DialogActions>
            </Dialog>

            <Footer />
        </DashboardLayout>
    );
};

export default Collections;